import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from 'src/app/services/user/user.service';
import { PlanService } from '../../plan.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import * as alertifyjs from 'alertifyjs';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-plans-wizard-edit',
  templateUrl: './plans-wizard-edit.component.html',
  styleUrls: ['./plans-wizard-edit.component.css']
})
export class PlansWizardEditComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Control del wizard
  currentStep = 1;
  totalSteps = 8;
  completedSteps: number[] = [];

  // Parámetro de la ruta
  planCode: string = '';
  originalPlanData: any = null;

  // CONFIGURACIÓN DE NAVEGACIÓN
  /**
   * Control de navegación del wizard
   *
   * true = Navegación secuencial obligatoria (producción)
   * false = Navegación libre para pruebas (desarrollo)
   */
  enforceSequentialNavigation = false; // Navegación libre para edición

  // Formularios por paso
  step1Form!: FormGroup; // Etiquetas
  step2Form!: FormGroup; // Fees (NUEVO)
  step3Form!: FormGroup; // Productos
  step4Form!: FormGroup; // Datos Básicos
  step5Form!: FormGroup; // Datos Generales
  step6Form!: FormGroup; // Filtros
  step7Form!: FormGroup; // Otros
  step8Form!: FormGroup; // Finalizar

  // Estados de carga
  isLoading = false;
  isSaving = false;
  isLoadingPlan = true;

  // Datos para Paso 1 - Etiquetas
  availableTags: string[] = [];
  filteredTags: string[] = [];
  tagSearchTerm = '';
  showTagDropdown = false;
  tagsLoading = false;

  // Datos para Paso 2 - Productos
  availableProducts: any[] = []; // Productos padre disponibles
  filteredProducts: any[] = []; // Productos filtrados por búsqueda
  productSearchTerm = '';
  showProductDropdown = false;
  productsLoading = false;

  // Identificadores del producto seleccionado
  availableIdentifiers: any[] = []; // Identificadores del producto seleccionado
  filteredIdentifiers: any[] = []; // Identificadores filtrados por búsqueda
  identifierSearchTerm = '';
  showIdentifierDropdown = false;
  identifiersLoading = false;

  // Selección temporal
  selectedProduct: any = null; // Producto padre seleccionado temporalmente
  selectedIdentifier: any = null; // Identificador seleccionado temporalmente

  // Lista final de productos agregados al plan
  addedProducts: any[] = []; // Array de {product, identifier} agregados al plan

  // Propiedades para propuestas de seguros
  insuranceProposals: any[] = [];
  private _selectedProposal: string = '';
  showInsuranceProposals: boolean = false;
  loadingInsuranceProposals: boolean = false; // Estado de carga de propuestas
  selectedProductInsuranceConfig: any = null;

  // Estado para mantener la propuesta seleccionada durante navegación
  private savedProposalState: { [productCode: string]: string } = {};

  // Getter y setter para selectedProposal con logging
  get selectedProposal(): string {
    return this._selectedProposal;
  }

  set selectedProposal(value: string) {
    const oldValue = this._selectedProposal;
    this._selectedProposal = value;

    // Guardar el estado cuando cambie
    if (this.selectedProduct && value !== oldValue) {
      const productKey = `${this.selectedProduct.code}_${this.selectedIdentifier?.code || 'no_identifier'}`;
      this.savedProposalState[productKey] = value;
      console.log(`💾 Estado guardado - Producto: ${this.selectedProduct.code}, Identificador: ${this.selectedIdentifier?.code}, Propuesta: ${value}`);
    }
  }

  // Debug logs para rastrear el flujo
  debugLogs: string[] = [];
  // Datos para Paso 2 - Fees
  availableFees: any[] = [];
  filteredFees: any[] = [];
  feeSearchTerm = '';
  showFeeDropdown = false;
  feesLoading = false;
  selectedFeeToAdd: any = null;
  selectedListType: 'white_list' | 'black_list' = 'black_list';

  // Configuración de pasos
  wizardSteps = [
    {
      id: 1,
      title: 'Etiquetas',
      description: 'Define la audiencia del plan',
      icon: 'fas fa-tags',
      required: true
    },
    {
      id: 2,
      title: 'Penalidades',
      description: 'Configura fees del plan',
      icon: 'fas fa-dollar-sign',
      required: false
    },
    {
      id: 3,
      title: 'Productos',
      description: 'Selecciona tipos de producto',
      icon: 'fas fa-box',
      required: true
    },
    {
      id: 4,
      title: 'Datos Básicos',
      description: 'Información fundamental del plan',
      icon: 'fas fa-info-circle',
      required: true
    },
    {
      id: 5,
      title: 'Datos Generales',
      description: 'Configuración financiera',
      icon: 'fas fa-calculator',
      required: true
    },
    {
      id: 6,
      title: 'Filtros',
      description: 'Criterios de elegibilidad',
      icon: 'fas fa-filter',
      required: false
    },
    {
      id: 7,
      title: 'Otros',
      description: 'Configuraciones adicionales',
      icon: 'fas fa-cog',
      required: false
    },
    {
      id: 8,
      title: 'Finalizar',
      description: 'Revisión y confirmación',
      icon: 'fas fa-check-circle',
      required: true
    }
  ];

  // Glosario contextual por paso
  currentStepGlossary: any[] = [];

  // Variables específicas para filtros
  availableLocations: any[] = []; // Ubicaciones disponibles
  filteredLocations: any[] = []; // Ubicaciones filtradas por búsqueda
  locationSearchTerm = '';
  showLocationDropdown = false;
  locationsLoading = false;

  // Variables para filtros de tienda
  availableStores: any[] = [];
  filteredStores: any[] = [];
  storeSearchTerm: string = '';
  showStoreDropdown: boolean = false;
  storesLoading = false;

  // Variables para filtros de productos del catálogo
  filteredCatalogProducts: any[] = [];
  catalogProductSearchTerm: string = '';
  showCatalogProductDropdown: boolean = false;
  catalogProductsLoading = false;

  // Variables para posicionamiento dinámico del dropdown
  dropdownTop: number = 0;
  dropdownLeft: number = 0;
  dropdownWidth: number = 300;

  // Variables para PaymentTermsBuilder
  paymentTerms: any[] = [];
  paymentTermsValid: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private planService: PlanService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeForms();
    this.setupFrequencyValidations(); // Configurar validaciones dinámicas
    this.loadAvailableTags();
    this.loadAvailableFees();
    this.loadAvailableProducts();
    this.updateStepGlossary();

    // TODO: TEMPORAL - Cambiar de vuelta a 1 cuando termine el debug
    this.currentStep = 1;

    // Obtener código del plan desde la ruta
    this.route.params.subscribe(params => {
      this.planCode = params['code'];
      if (this.planCode) {
        // Cargar tiendas PRIMERO, luego cargar el plan
        this.loadAvailableStores().then(() => {
          console.log('🏪 Tiendas cargadas, ahora cargando plan...');
          this.loadPlanData();
        }).catch(() => {
          console.warn('⚠️ Error al cargar tiendas, cargando plan de todas formas...');
          this.loadPlanData();
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Cargar datos del plan existente desde el backend
   */
  private loadPlanData(): void {
    console.log('🔄 Iniciando carga del plan:', this.planCode);

    if (!this.planCode) {
      console.error('❌ No se proporcionó código de plan');
      alert('Error: No se proporcionó código de plan');
      this.router.navigate(['/console/plans']);
      return;
    }

    this.isLoadingPlan = true;

    // Obtener datos del plan por código
    this.planService.getPlanByCode(this.planCode).subscribe({
      next: (response: any) => {
        console.log('📡 Respuesta del servicio:', response);

        if (response && response.res) {
          this.originalPlanData = response.res;

          console.log('✅ Plan encontrado:', {
            code: this.originalPlanData.code,
            name: this.originalPlanData.name,
            type: this.originalPlanData.params?.type
          });

          try {
            // Convertir datos de formato backend a formato wizard
            const wizardData = this.convertDatabaseToWizardFormat(this.originalPlanData);

            // Precargar formularios con los datos
            this.preloadFormsWithData(wizardData);

            console.log('✅ Plan cargado exitosamente:', this.planCode);
            console.log('📋 Datos originales:', this.originalPlanData);
            console.log('🔄 Datos convertidos:', wizardData);

            this.isLoadingPlan = false;
          } catch (conversionError) {
            console.error('❌ Error en conversión de datos:', conversionError);
            alert(`Error al procesar datos del plan: ${conversionError}`);
            this.isLoadingPlan = false;
            // No redirigir inmediatamente, dar tiempo para ver el error
            setTimeout(() => {
              this.router.navigate(['/console/plans']);
            }, 3000);
          }
        } else if (response && !response.res) {
          console.warn('⚠️ Respuesta sin datos:', response);
          alert(`Plan "${this.planCode}" no encontrado en el sistema`);
          this.isLoadingPlan = false;
          setTimeout(() => {
            this.router.navigate(['/console/plans']);
          }, 2000);
        } else {
          console.error('❌ Respuesta inválida del servidor:', response);
          alert('Error: Respuesta inválida del servidor');
          this.isLoadingPlan = false;
          setTimeout(() => {
            this.router.navigate(['/console/plans']);
          }, 2000);
        }
      },
      error: (error) => {
        console.error('❌ Error al cargar el plan:', error);

        let errorMessage = 'Error desconocido';
        if (error.status === 404) {
          errorMessage = `Plan "${this.planCode}" no encontrado`;
        } else if (error.status === 403) {
          errorMessage = 'No tienes permisos para acceder a este plan';
        } else if (error.status === 500) {
          errorMessage = 'Error interno del servidor';
        } else if (error.message) {
          errorMessage = error.message;
        }

        alert(`Error al cargar el plan: ${errorMessage}`);
        this.isLoadingPlan = false;

        // Dar tiempo para que el usuario vea el error
        setTimeout(() => {
          this.router.navigate(['/console/plans']);
        }, 3000);
      }
    });
  }

  /**
   * Convertir datos de formato backend a formato wizard interno
   * Este es el proceso inverso de getDatabaseFormatJSON()
   */
  private convertDatabaseToWizardFormat(databaseData: any): any {
    console.log('🔄 Convirtiendo datos de base de datos a formato wizard:', databaseData);

    // Determinar el tipo de plan basado en params.type
    // Soporta 3 tipos: 'CONTADO', 'CUOTA', 'CREDITO'
    const paramType = databaseData.params?.type?.toUpperCase();
    let planType: string;

    if (paramType === 'CONTADO') {
      planType = 'cash';
    } else if (paramType === 'SUSCRIPCION') {
      planType = 'suscripcion';
    } else {
      // 'CREDITO' o undefined → 'credit' (default)
      planType = 'credit';
    }

    console.log('📋 Tipo de plan detectado:', { paramType, planType });

    // Extraer códigos dinámicos (etiquetas)
    const dinamicCode = databaseData.params?.filters?.dinamic_code;
    let tagsMode = '';
    let selectedTags: string[] = [];

    if (dinamicCode?.person) {
      tagsMode = dinamicCode.person.type === 'white_list' ? 'whitelist' : 'blacklist';
      selectedTags = dinamicCode.person.list || [];
    }

    console.log('🏷️ Tags extraídos:', { tagsMode, selectedTags });

    // Extraer productos e información de seguros
    const productType = databaseData.params?.product_type || '';
    const insuranceInfo = databaseData.params?.insurance || {};
    
    const addedProducts = productType ? [{
      product: { 
        code: productType, 
        name: productType,
        // Incluir información de seguros del plan si existe
        insurance: {
          is_insurance_product: insuranceInfo.product ? true : false, // Si tiene insurance.product, es de seguros
          insurer_code: insuranceInfo.brokerage || null
        }
      },
      identifier: { 
        code: productType, 
        name: productType,
        insurance_product: insuranceInfo.product || null // Código del producto de seguro (AP, RCV, etc.)
      },
      // Construir objeto completo de propuesta si existe
      insuranceProposal: (() => {
        if (insuranceInfo.plan) {
          // Si existe proposal_name, usarlo directamente
          if (insuranceInfo.proposal_name) {
            return {
              code: insuranceInfo.plan,
              name: insuranceInfo.proposal_name, // Incluye formato "Nombre - Prima"
              mtPrima: insuranceInfo.mtPrima || null
            };
          }
          // Si no existe proposal_name, devolver solo el código (retrocompatibilidad)
          return insuranceInfo.plan;
        }
        return null;
      })()
    }] : [];

    // Debug de productos
    console.log('📦 Productos desde DB:', {
      productType: productType,
      insuranceInfo: insuranceInfo,
      addedProducts: addedProducts,
      isEmpty: addedProducts.length === 0,
      IMPORTANTE_PARA_PLANES_ANTIGUOS: 'Si addedProducts está vacío, el plan antiguo no tiene product_type configurado'
    });

    // Convertir fechas al formato correcto (YYYY-MM-DD)
    const formatDateForInput = (dateString: string): string => {
      if (!dateString) return '';

      try {
        // Si la fecha viene en formato ISO, extraer solo la parte de fecha
        if (dateString.includes('T')) {
          return dateString.split('T')[0];
        }

        // Si la fecha viene en formato DD/MM/YYYY, convertir a YYYY-MM-DD
        if (dateString.includes('/')) {
          const parts = dateString.split('/');
          if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            return `${year}-${month}-${day}`;
          }
        }

        // Si la fecha viene en formato YYYY-MM-DD, devolverla tal como está
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateString;
        }

        // Intentar crear una fecha y formatearla
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }

        return '';
      } catch (error) {
        console.warn('Error al formatear fecha:', dateString, error);
        return '';
      }
    };

    // Debug de fecha de inicio DESPUÉS de declarar la función
    const originalInitDate = databaseData.params?.init_date;
    const formattedInitDate = originalInitDate ? formatDateForInput(originalInitDate) : '';
    console.log('📅 Fecha de inicio:', { originalInitDate, formattedInitDate });

    // Debug de pago inicial
    const initialPaymentData = databaseData.initial;
    console.log('💰 Pago inicial desde DB:', {
      originalData: initialPaymentData,
      mapped: {
        required: initialPaymentData?.status || false,
        calculationType: initialPaymentData?.type || 'percentage',
        value: initialPaymentData?.amount || 0,
        recalculateInstallments: initialPaymentData?.recalculate || false
      }
    });

    // Debug de tipo de plan y step5
    const incluirStep5 = planType === 'credit' || planType === 'suscripcion';
    console.log('📋 Tipo de plan y Step 5:', {
      planType: planType,
      incluirStep5: incluirStep5,
      razon: incluirStep5 ? `Plan ${planType} - incluir step5_credit (Datos Generales)` : 'Plan de contado - omitir step5_credit'
    });

    // Extraer installmentOptions y frequency desde payment_terms (MOVIDO FUERA DEL BLOQUE CREDIT)
    let installmentOptions: number[] = [];
    let frequencyDays: number = 0;
    let frequency: string = '';

    // Convertir payment_terms al formato del PaymentTermsBuilder
    if (databaseData.payment_terms && Array.isArray(databaseData.payment_terms) && databaseData.payment_terms.length > 0) {
      // Si existen payment_terms, convertir al formato del builder
      const paymentTerms = databaseData.payment_terms;

      // Convertir cada payment term al formato del builder
      this.paymentTerms = paymentTerms.map((term: any) => ({
        installments: term.installments || 1,
        order: term.order || 0,
        frequency: term.frequency?.type || 'mensual',
        frequencyDays: term.frequency?.number || undefined
      }));

      // Mantener compatibilidad con el formulario antiguo (por si acaso)
      installmentOptions = paymentTerms.map((term: any) => term.installments || 1);

      if (paymentTerms[0] && paymentTerms[0].frequency && paymentTerms[0].frequency.type) {
        const dbFrequency = paymentTerms[0].frequency.type;
        frequency = this.mapDBFrequencyToWizardFormat(dbFrequency);

        // Extraer frequencyDays si es dias_continuos
        if (dbFrequency === 'dias_continuos' && paymentTerms[0].frequency.number) {
          frequencyDays = paymentTerms[0].frequency.number;
        }
      }

      console.log('✅ Payment terms cargados en builder:', this.paymentTerms);
      console.log('✅ Payment terms legacy:', { installmentOptions, frequency, originalDBFrequency: paymentTerms[0]?.frequency?.type });
      
      // Marcar como válidos si hay términos
      this.paymentTermsValid = this.paymentTerms.length > 0;
    } else {
      // FALLBACK: Si no hay payment_terms o están vacíos, usar valores por defecto lógicos
      console.log('⚠️ Payment terms vacíos o ausentes, aplicando fallback strategy');

      // Inferir valores por defecto basados en otros campos del plan
      // Si hay datos en 'initial', probablemente es un plan de cuotas
      if (databaseData.initial && databaseData.initial.status) {
        installmentOptions = [3]; // Valor por defecto común
        frequency = 'quincenal'; // Frecuencia por defecto común en formato wizard
      } else {
        installmentOptions = [1]; // Plan de pago único
        frequency = 'mensual';
      }

      console.log('🔧 Valores fallback aplicados:', { installmentOptions, frequency, frequencyDays: 0 });
      
      // Inicializar paymentTerms vacío
      this.paymentTerms = [];
      this.paymentTermsValid = false;
    }

    // Extraer filtros de persona y tienda
    const originalFilters = databaseData.params?.filters || {};
    const personFilters = originalFilters.general?.person || null;
    const storeFilters = dinamicCode?.store || null;

    // Extraer configuración de crédito (para 'credit' y 'suscripcion')
    let creditConfig = null;
    if (planType === 'credit' || planType === 'suscripcion') {
      console.log('🏦 Procesando datos de plan con configuración de cuotas desde DB:', {
        initial: databaseData.initial,
        params: databaseData.params,
        payment_terms: databaseData.params?.payment_terms,
        min_amount: databaseData.params?.min_amount,
        max_amount_base: databaseData.params?.max_amount_base,
        current_quota_min: databaseData.params?.current_quota_min,
        current_quota_max: databaseData.params?.current_quota_max
      });

      // Configuración de pago inicial
      const initialPayment = {
        required: databaseData.initial?.status || false,
        calculationType: databaseData.initial?.type || 'percentage',
        value: databaseData.initial?.amount || 0,
        recalculateInstallments: databaseData.initial?.recalculate || false
      };

      // Configuración de financiamiento
      const financing = {
        minPurchaseAmount: databaseData.params?.min_amount || 0,
        maxFinanceAmount: databaseData.params?.max_amount_base || 0
      };

      // Configuración de cupo - simplificada para coincidir con el componente de creación
      const quotaRequirements = {
        minQuota: databaseData.params?.current_quota_min || 0,
        maxQuota: databaseData.params?.current_quota_max || 0
      };

      creditConfig = {
        initialPayment,
        financing,
        installments: {
          options: installmentOptions.join(','), // Convertir array a string
          frequency: frequency
        },
        quotaRequirements,
        isValid: true
      };

      console.log('✅ Configuración de crédito construida:', creditConfig);
    }

    // Extraer configuración de filtros
    const filtersConfig: any = {
      personConfig: {
        enabled: true,
        personType: databaseData.params?.filters?.general?.person?.audience?.toLowerCase() || 'natural'
      },
      storeConfig: {
        enabled: false,
        filterType: '',
        selectedStores: []
      },
      productConfig: {
        enabled: false,
        filterType: '',
        matchMode: 'any',
        allowOthers: true,
        selectedProducts: []
      }
    };

    // Buscar filtros de tienda en múltiples ubicaciones
    const storeFiltersFromDynamic = dinamicCode?.store;
    const storeFiltersFromGeneral = databaseData.params?.filters?.general?.invoices?.shop;

    // Configurar filtros de tienda si existen (priorizar general.invoices.shop)
    const activeStoreFilters = storeFiltersFromGeneral || storeFiltersFromDynamic;

    if (activeStoreFilters) {
      filtersConfig.storeConfig.enabled = true;
      filtersConfig.storeConfig.filterType = activeStoreFilters.type === 'white_list' ? 'whitelist' : 'blacklist';
      filtersConfig.storeConfig.selectedStores = activeStoreFilters.list || [];

      console.log('🏪 Filtros de tienda encontrados:', {
        source: storeFiltersFromGeneral ? 'general.invoices.shop' : 'dinamic_code.store',
        type: activeStoreFilters.type,
        storeIds: activeStoreFilters.list,
        mapped: filtersConfig.storeConfig
      });
    } else {
      console.log('🏪 No se encontraron filtros de tienda');
    }

    // Cargar filtros de productos del catálogo si existen
    const productFilter = databaseData.params?.filters?.product_filter;
    if (productFilter && productFilter.type && productFilter.list?.length > 0) {
      filtersConfig.productConfig.enabled = true;
      filtersConfig.productConfig.filterType = productFilter.type;
      filtersConfig.productConfig.matchMode = productFilter.match_mode || (productFilter.strict_match ? 'all' : 'any');
      filtersConfig.productConfig.allowOthers = productFilter.allow_others !== false;
      // Inicializar con códigos, luego resolver nombres vía API
      filtersConfig.productConfig.selectedProducts = productFilter.list.map((code: string) => ({
        code: code,
        name: code,
        brand: '',
        category: ''
      }));
      console.log('📦 Filtros de productos del catálogo encontrados:', filtersConfig.productConfig);

      // Resolver nombres reales de productos desde el catálogo
      this.resolveCatalogProductNames(productFilter.list);
    }

    // Extraer fees (penalidades)
    const feesData = databaseData.params?.fees?.apply || [];
    const whiteListFees = feesData.find((item: any) => item.type === 'white_list')?.list || [];
    const blackListFees = feesData.find((item: any) => item.type === 'black_list')?.list || [];

    const wizardData = {
      // Metadatos del wizard
      _wizard: {
        mode: 'edit',
        currentPlan: databaseData,
        originalCode: databaseData.code
      },

      // Paso 1: Tags
      step1_tags: {
        filterMode: tagsMode,
        selectedTags: selectedTags
      },

      // Paso 2: Fees (Penalidades)
      step2_fees: {
        whiteListFees: whiteListFees.filter(Boolean),
        blackListFees: blackListFees.filter(Boolean)
      },

      // Paso 3: Productos
      step3_products: {
        selectedProducts: addedProducts
      },
      _addedProducts: addedProducts, // Lista auxiliar para productos

      // Paso 4: Datos básicos
      step4_basic: {
        planType: planType,
        planName: databaseData.name || '',
        planCode: databaseData.code || '',
        description: databaseData.description || '',
        status: databaseData.status ?? false,
        priority: databaseData.params?.priority ?? 0,
        icon: databaseData.params?.icon || 'fas fa-star',
        startDate: formattedInitDate,
        endDate: null,
        multicompra: databaseData.params?.multiple_invoices?.status || false
      },

      // Paso 5: Configuración de crédito (PARA PLANES DE CRÉDITO Y SUSCRIPCIÓN)
      ...((planType === 'credit' || planType === 'suscripcion') && {
        step5_credit: {
          initialPayment: {
            required: databaseData.initial?.status || false,
            calculationType: databaseData.initial?.type || 'percentage',
            value: databaseData.initial?.amount || 0,
            recalculateInstallments: databaseData.initial?.recalculate || false
          },
          financing: {
            minPurchaseAmount: databaseData.params?.min_amount || 0,
            maxFinanceAmount: databaseData.params?.max_amount_base || 0
          },
          installments: {
            options: installmentOptions.join(','), // Convertir array a string
            frequency: frequency,
            frequencyDays: frequencyDays,
          },
          quotaRequirements: {
            minQuota: databaseData.params?.current_quota_min || 0,
            maxQuota: databaseData.params?.current_quota_max || 1000
          }
        }
      }),

      // Paso 6: Filtros
      step6_filters: {
        personConfig: {
          enabled: !!personFilters,
          audience: personFilters?.audience || 'NATURAL',
          ageRange: personFilters?.age_range || null,
          minSalary: personFilters?.min_salary || null,
          maxSalary: personFilters?.max_salary || null,
          allowEmployeesFromProviders: personFilters?.allow_employees_from_providers || false
        },
        storeConfig: {
          enabled: !!activeStoreFilters,
          filterType: activeStoreFilters?.type || 'white_list',
          selectedStores: activeStoreFilters?.list || []
        }
      },

      // Paso 7: Otros
      step7_others: {
        electronicPromissoryNote: databaseData.params?.generate_pagares || false,
        physicalPromissoryNote: databaseData.params?.generate_pagares_fisico || false,
        allowWeekendsAndHolidays: databaseData.params?.weekday || false,
        clientVisible: databaseData.params?.client_visible ?? true // Por defecto visible si no existe
      },

      // Configuración adicional (para referencia)
      _originalFilters: originalFilters,
      _personFilters: personFilters,
      _storeFilters: activeStoreFilters || null
    };

    console.log('✅ Datos convertidos para wizard (con fallback):', wizardData);
    return wizardData;
  }

  private mapDBFrequencyToWizardFormat(dbFrequency: string): string {
    switch (dbFrequency) {
      case 'semanal':
        return 'semanal';
      case 'quincenal':
        return 'quincenal';
      case 'mensual':
        return 'mensual';
      case 'dias_continuos':
        return 'dias_continuos';
      default:
        return 'mensual';
    }
  }

  /**
   * Precargar formularios con datos convertidos
   */
  private preloadFormsWithData(wizardData: any): void {
    console.log('🔄 Precargando formularios con datos:', wizardData);

    // Paso 1: Etiquetas
    this.step1Form.patchValue({
      filterMode: wizardData.step1_tags.filterMode,
      selectedTags: wizardData.step1_tags.selectedTags
    });

    // Paso 2: Fees (Penalidades)
    this.step2Form.patchValue({
      whiteListFees: wizardData.step2_fees?.whiteListFees || [],
      blackListFees: wizardData.step2_fees?.blackListFees || []
    });

    // Paso 3: Productos
    console.log('📦 Precargando productos:', {
      originalSelectedProducts: wizardData.step3_products.selectedProducts,
      originalAddedProducts: wizardData._addedProducts
    });

    // Extraer códigos de identificador para el formulario
    const identifierCodes = wizardData.step3_products.selectedProducts.map((item: any) => item.identifier?.code).filter(Boolean);

    this.step3Form.patchValue({
      selectedProducts: identifierCodes
    });
    this.addedProducts = wizardData._addedProducts || [];

    console.log('📦 Productos precargados:', {
      formValue: identifierCodes,
      addedProductsCount: this.addedProducts.length,
      addedProducts: this.addedProducts
    });

    // Restaurar propuestas de seguros si existen
    if (this.addedProducts.length > 0) {
      const firstProduct = this.addedProducts[0];
      if (firstProduct.insuranceProposal) {
        console.log('📋 Restaurando propuesta existente desde producto agregado:', firstProduct.insuranceProposal);

        // Guardar la propuesta en el estado para que se mantenga durante navegación
        const productKey = `${firstProduct.product.code}_${firstProduct.identifier?.code || 'no_identifier'}`;
        this.savedProposalState[productKey] = firstProduct.insuranceProposal;
        this.selectedProposal = firstProduct.insuranceProposal;

        // También necesitamos verificar si el producto es de seguros para mostrar el select
        this.checkAndRestoreInsuranceProposals(firstProduct.product);
      }
      
      // Actualizar validaciones de prioridad según tipo de producto cargado
      this.updatePriorityValidation();
    }

    // Paso 3: Productos (ya restaurado arriba con addedProducts)
    
    // Paso 4: Datos Básicos
    this.step4Form.patchValue({
      planType: wizardData.step4_basic.planType,
      planName: wizardData.step4_basic.planName,
      planCode: wizardData.step4_basic.planCode,
      description: wizardData.step4_basic.description,
      status: wizardData.step4_basic.status ? 'true' : 'false',
      priority: wizardData.step4_basic.priority,
      icon: wizardData.step4_basic.icon,
      startDate: wizardData.step4_basic.startDate,
      endDate: wizardData.step4_basic.endDate,
      multicompra: wizardData.step4_basic.multicompra
    });

    // Deshabilitar campos que no se pueden modificar en edición
    this.step4Form.get('planCode')?.disable();
    this.step4Form.get('startDate')?.disable();
    this.step4Form.get('planType')?.disable(); // Deshabilitar tipo de plan también

    // Agregar propiedad para identificar campos deshabilitados en el template
    this.step4Form.get('planCode')?.markAsTouched();
    this.step4Form.get('startDate')?.markAsTouched();
    this.step4Form.get('planType')?.markAsTouched();

    // Paso 5: Configuración de Crédito (solo si es crédito)
    if (wizardData.step5_credit) {
      this.configureCreditPlan();

      // 2. Popular el formulario con los datos de crédito.
      console.log('🔄 Precargando datos del paso 5 (crédito):', wizardData.step5_credit);
      console.log('🔍 DEBUG - Installments data:', {
        options: wizardData.step5_credit.installments?.options,
        frequency: wizardData.step5_credit.installments?.frequency,
        frequencyDays: wizardData.step5_credit.installments?.frequencyDays
      });
      this.step5Form.patchValue({
        initialPayment: wizardData.step5_credit.initialPayment,
        financing: wizardData.step5_credit.financing,
        installments: wizardData.step5_credit.installments,
        quotaRequirements: wizardData.step5_credit.quotaRequirements
      });
      console.log('🔍 DEBUG - Step5Form after patch:', {
        options: this.step5Form.get('installments.options')?.value,
        frequency: this.step5Form.get('installments.frequency')?.value,
        valid: this.step5Form.get('installments')?.valid,
        errors: this.step5Form.get('installments.options')?.errors
      });
      
      // Actualizar validadores del campo 'value' según el estado de 'required' después de cargar los datos
      this.onFinancingFieldChange();
    }

    // Decidir qué configuración aplicar DESPUÉS de que todos los datos han sido parchados.
    // La decisión se basa en el `planType` que ya está en el step4Form.
    if (this.isPlanTypeCredit()) {
      console.log('✅ El formulario contiene datos de crédito, aplicando configuración de CRÉDITO.');
      this.configureCreditPlan();
    } else {
      console.log('💰 El formulario contiene datos de contado, aplicando configuración de CONTADO.');
      this.configureCashPlan();
    }

    // Paso 6: Filtros
    console.log('🏪 Precargando filtros de tienda:', {
      storeConfigEnabled: wizardData.step6_filters.storeConfig.enabled,
      storeFilterType: wizardData.step6_filters.storeConfig.filterType,
      selectedStores: wizardData.step6_filters.storeConfig.selectedStores,
      selectedStoresCount: wizardData.step6_filters.storeConfig.selectedStores?.length || 0
    });

    this.step6Form.patchValue({
      personConfig: wizardData.step6_filters.personConfig,
      storeConfig: {
        enabled: wizardData.step6_filters.storeConfig.enabled,
        filterType: wizardData.step6_filters.storeConfig.filterType,
        selectedStores: [] // Se cargará después por loadStoreFiltersFromData
      },
      productConfig: {
        enabled: wizardData.step6_filters.productConfig?.enabled || false,
        filterType: wizardData.step6_filters.productConfig?.filterType || '',
        matchMode: wizardData.step6_filters.productConfig?.matchMode || 'any',
        allowOthers: wizardData.step6_filters.productConfig?.allowOthers !== false,
        selectedProducts: wizardData.step6_filters.productConfig?.selectedProducts || []
      }
    });

    // Configurar tipo de filtro principal
    if (wizardData.step6_filters.personConfig.enabled) {
      this.step6Form.patchValue({ filterType: 'person' });
    } else if (wizardData.step6_filters.storeConfig.enabled) {
      this.step6Form.patchValue({ filterType: 'store' });
    }

    // Paso 7: Otros
    this.step7Form.patchValue({
      electronicPromissoryNote: wizardData.step7_others.electronicPromissoryNote,
      physicalPromissoryNote: wizardData.step7_others.physicalPromissoryNote,
      allowWeekendsAndHolidays: wizardData.step7_others.allowWeekendsAndHolidays,
      clientVisible: wizardData.step7_others.clientVisible ?? true // Por defecto visible si no existe
    });

    // Paso 8: Finalizar
    this.step8Form.patchValue({
      termsAccepted: false, // Siempre requiere nueva aceptación
      dataConfirmed: false  // Siempre requiere nueva confirmación
    });

    // Marcar todos los pasos como completados para navegación libre en edición
    this.completedSteps = [1, 2, 3, 4, 5, 6, 7, 8];

    // Cargar filtros de tienda si están configurados
    console.log('🏪 Verificando _storeFilters para cargar tiendas:', {
      storeFilters: wizardData._storeFilters,
      hasStoreFilters: !!wizardData._storeFilters,
      storeFiltersList: wizardData._storeFilters?.list,
      storeFiltersType: wizardData._storeFilters?.type
    });

    if (wizardData._storeFilters) {
      console.log('🏪 Llamando loadStoreFiltersFromData...');
      this.loadStoreFiltersFromData(wizardData._storeFilters);
    } else {
      console.log('🏪 No hay _storeFilters para cargar');
    }

    console.log('✅ Formularios precargados exitosamente');
    console.log('📋 Estado de formularios:', {
      step1Valid: this.step1Form.valid,
      step2Valid: this.step2Form.valid,
      step3Valid: this.step3Form.valid,
      step4Valid: this.step4Form.valid,
      step5Valid: this.step5Form.valid,
      step6Valid: this.step6Form.valid,
      step7Valid: this.step7Form.valid,
      step8Valid: this.step8Form.valid
    });

    console.log('📅 Fechas cargadas:', {
      startDate: wizardData.step4_basic.startDate,
      endDate: wizardData.step4_basic.endDate,
      startDateControl: this.step4Form.get('startDate')?.value,
      endDateControl: this.step4Form.get('endDate')?.value
    });
  }

  private async loadStoreFiltersFromData(storeFilters: any): Promise<void> {
    console.log('🏪 [loadStoreFiltersFromData] Iniciando carga con datos:', storeFilters);

    if (!storeFilters || !storeFilters.list || !Array.isArray(storeFilters.list)) {
      console.log('🏪 [loadStoreFiltersFromData] Datos inválidos:', {
        hasStoreFilters: !!storeFilters,
        hasList: !!storeFilters?.list,
        isListArray: Array.isArray(storeFilters?.list),
        storeFilters: storeFilters
      });
      return;
    }

    console.log('🏪 [loadStoreFiltersFromData] Datos válidos, esperando tiendas...', {
      storeIds: storeFilters.list,
      availableStoresCount: this.availableStores.length
    });

    // Esperar a que las tiendas estén cargadas
    let attempts = 0;
    const maxAttempts = 20; // Aumentado de 10 a 20
    const waitTime = 300; // Reducido de 500 a 300ms para verificar más frecuentemente

    while (this.availableStores.length === 0 && attempts < maxAttempts) {
      console.log(`🏪 [loadStoreFiltersFromData] Intento ${attempts + 1}/${maxAttempts} - Esperando tiendas...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      attempts++;
    }

    // Si todavía no hay tiendas, intentar cargar forzadamente
    if (this.availableStores.length === 0) {
      console.log('🏪 [loadStoreFiltersFromData] ⚠️ Intentando cargar tiendas forzadamente...');
      try {
        await this.loadAvailableStores();
        console.log('🏪 [loadStoreFiltersFromData] ✅ Tiendas cargadas forzadamente:', this.availableStores.length);
      } catch (error) {
        console.error('🏪 [loadStoreFiltersFromData] ❌ Error al cargar tiendas forzadamente:', error);
      }
    }

    if (this.availableStores.length === 0) {
      console.warn('🏪 [loadStoreFiltersFromData] ⚠️ No se pudieron cargar las tiendas después de todos los intentos');
      return;
    }

    console.log('🏪 [loadStoreFiltersFromData] Tiendas disponibles:', {
      count: this.availableStores.length,
      firstFew: this.availableStores.slice(0, 3).map((store: any) => ({id: store.id, name: store.name}))
    });

    // Buscar las tiendas por ID
    const selectedStores = storeFilters.list
      .map((storeId: string) => {
        // Comparar tanto string como number para compatibilidad
        const foundStore = this.availableStores.find((store: any) =>
          String(store.id) === String(storeId)
        );
        console.log(`🏪 [loadStoreFiltersFromData] Buscando tienda ID "${storeId}":`, foundStore ? `Encontrada: ${foundStore.name}` : 'No encontrada');
        return foundStore;
      })
      .filter(Boolean); // Remover valores undefined

    console.log('🏪 [loadStoreFiltersFromData] Tiendas seleccionadas encontradas:', {
      totalRequested: storeFilters.list.length,
      totalFound: selectedStores.length,
      foundStores: selectedStores.map((store: any) => ({id: store.id, name: store.name}))
    });

    if (selectedStores.length > 0) {
      this.step5Form.patchValue({
        storeConfig: {
          selectedStores: selectedStores
        }
      });
      console.log('✅ [loadStoreFiltersFromData] Filtros de tienda cargados exitosamente:', selectedStores.length, 'tiendas');
    } else {
      console.warn('⚠️ [loadStoreFiltersFromData] No se encontraron tiendas válidas para cargar');
    }
  }

  private initializeForms(): void {
    // Paso 1: Etiquetas
    this.step1Form = this.fb.group({
      filterMode: [''], // 'whitelist' | 'blacklist' - Opcional
      selectedTags: [[], this.validateTags.bind(this)]
    });

    // Paso 2: Fees (Penalidades)
    this.step2Form = this.fb.group({
      whiteListFees: [[]], // Array de códigos de fees en lista blanca
      blackListFees: [[]]  // Array de códigos de fees en lista negra
    });

    // Paso 3: Productos
    this.step3Form = this.fb.group({
      selectedProducts: [[], Validators.required] // Array de identificadores seleccionados
    });

    // Paso 4: Datos Básicos
    this.step4Form = this.fb.group({
      planType: ['', Validators.required], // 'cash' | 'credit'
      status: ['false', Validators.required], // 'true' | 'false' - Inactivo por defecto
      planName: ['', [Validators.required, Validators.maxLength(100)]],
      planCode: ['', [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(/^[A-Z0-9_-]+$/i) // Solo letras, números, guiones y guiones bajos
      ]],
      description: ['', Validators.maxLength(1000)],
      icon: ['fas fa-star'], // Valor por defecto
      priority: [0, [Validators.min(0), Validators.max(999)]], // Sin Validators.required - es opcional para seguros
      multicompra: [false], // Boolean - false por defecto
      startDate: ['', Validators.required],
      endDate: ['', this.validateEndDate.bind(this)]
    });

    // Paso 5: Datos Generales (solo si es crédito)
    this.step5Form = this.fb.group({
      // Pago Inicial
      initialPayment: this.fb.group({
        required: [true, Validators.required],
        calculationType: ['percentage', Validators.required], // 'percentage' | 'absolute'
        value: [0, [Validators.required, Validators.min(0)]],
        recalculateInstallments: [true]
      }),

      // Montos de Financiamiento
      financing: this.fb.group({
        minPurchaseAmount: [0, [Validators.required, Validators.min(0.01)]],
        maxFinanceAmount: [0, [Validators.required, Validators.min(0.01)]]
      }),

      // Plazos y Cuotas
      installments: this.fb.group({
        options: ['', [Validators.required, Validators.pattern(/^[0-9]+(,[0-9]+)*$/)]], // String con números separados por coma: "2,4,6"
        frequency: ['mensual', Validators.required], // 'semanal' | 'quincenal' | 'mensual' | 'dias_continuos'
        frequencyDays: [0] // Número de días para dias_continuos - validaciones dinámicas
      }),

      // Requisitos de Cupo - estructura simplificada como en el componente de creación
      quotaRequirements: this.fb.group({
        minQuota: [0],
        maxQuota: [0]
      })
    });

    // Paso 6: Filtros
    this.step6Form = this.fb.group({
      filterType: [''], // 'person' | 'store' - Tipo de filtro principal

      // Configuración por Persona
      personConfig: this.fb.group({
        enabled: [false],
        personType: ['natural'] // Valores: 'natural' o 'juridica'
      }),

      // Configuración por Tienda
      storeConfig: this.fb.group({
        enabled: [false],
        filterType: [''], // 'whitelist' | 'blacklist'
        selectedStores: [[]] // Array de tiendas seleccionadas
      }),

      // Configuración por Productos del Catálogo
      productConfig: this.fb.group({
        enabled: [false],
        filterType: [''], // 'white_list' | 'black_list'
        matchMode: ['any'], // 'all' = todos deben coincidir, 'any' = al menos uno
        allowOthers: [true], // true = permite productos fuera de la lista, false = solo productos de la lista
        selectedProducts: [[]] // Array de productos seleccionados {code, name, brand, category}
      })
    });

    // Paso 7: Otros
    this.step7Form = this.fb.group({
      electronicPromissoryNote: [false],      // Pagaré Electrónico
      physicalPromissoryNote: [false],        // Pagaré Físico
      allowWeekendsAndHolidays: [false],      // Diferir cuotas en feriados
      clientVisible: [true]                   // Visibilidad del plan al cliente (por defecto visible)
    });

    // Paso 8: Finalizar
    this.step8Form = this.fb.group({
      termsAccepted: [false, Validators.requiredTrue],
      dataConfirmed: [false, Validators.requiredTrue]
    });
  }

  /**
   * Configurar validaciones dinámicas para frequencyDays
   */
  private setupFrequencyValidations(): void {
    const frequencyControl = this.step5Form?.get('installments.frequency');
    const frequencyDaysControl = this.step5Form?.get('installments.frequencyDays');

    if (frequencyControl && frequencyDaysControl) {
      // Escuchar cambios en frequency para actualizar validaciones de frequencyDays
      frequencyControl.valueChanges.subscribe(frequency => {
        this.updateFrequencyDaysValidation(frequency);
      });

      // Configurar validación inicial
      this.updateFrequencyDaysValidation(frequencyControl.value);
    }
  }

  /**
   * Actualizar validaciones de frequencyDays según la frecuencia seleccionada
   */
  private updateFrequencyDaysValidation(frequency: string): void {
    const frequencyDaysControl = this.step5Form?.get('installments.frequencyDays');

    if (frequencyDaysControl) {
      // Limpiar validaciones existentes
      frequencyDaysControl.clearValidators();

      if (frequency === 'dias_continuos') {
        // Si es dias_continuos, agregar validaciones obligatorias
        frequencyDaysControl.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(300)
        ]);
      } else {
        // Para otras frecuencias, no hay validaciones (valor se ignora)
        frequencyDaysControl.setValue(0);
      }

      // Actualizar validación
      frequencyDaysControl.updateValueAndValidity();
    }
  }

  private validateTags(control: any) {
    const filterMode = this.step1Form?.get('filterMode')?.value;
    const selectedTags = control.value || [];

    if (filterMode === 'whitelist' && selectedTags.length === 0) {
      return { required: true };
    }
    return null;
  }

  // Métodos de navegación del wizard (copiados del componente original)
  getVisibleSteps(): any[] {
    return this.wizardSteps
      .map((step, index) => ({
        number: index + 1,
        title: step.title,
        description: step.description,
        icon: step.icon,
        required: step.required
      }))
      .filter(step => !this.isStepHidden(step.number));
  }

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      // No permitir navegación al paso 5 si está oculto (plan de contado)
      if (step === 5 && this.isStepHidden(5)) {
        console.log('⚠️ No se puede navegar al paso 5 (Datos Generales) en planes de contado');
        return;
      }

      this.currentStep = step;
      this.updateStepGlossary();
    }
  }

  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      let nextStep = this.currentStep + 1;

      // Saltar paso 5 (Datos Generales) si es plan de contado
      if (nextStep === 5 && this.isStepHidden(5)) {
        nextStep = 6;
      }

      this.currentStep = Math.min(nextStep, this.totalSteps);
      this.updateStepGlossary();
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      let prevStep = this.currentStep - 1;

      // Saltar paso 5 (Datos Generales) si es plan de contado
      if (prevStep === 5 && this.isStepHidden(5)) {
        prevStep = 4;
      }

      this.currentStep = Math.max(prevStep, 1);
      this.updateStepGlossary();
    }
  }

  canGoToNextStep(): boolean {
    return this.isCurrentStepValid();
  }

  isCurrentStepValid(): boolean {
    // Usar validaciones específicas de cada paso en lugar de solo verificar el formulario
    return this.isStepValidForCompletion(this.currentStep);
  }

  getCurrentStepForm(): FormGroup | null {
    switch (this.currentStep) {
      case 1: return this.step1Form;
      case 2: return this.step2Form;
      case 3: return this.step3Form;
      case 4: return this.step4Form;
      case 5: return this.step5Form;
      case 6: return this.step6Form;
      case 7: return this.step7Form;
      default: return null;
    }
  }

  isStepCompleted(stepNumber: number): boolean {
    return this.completedSteps.includes(stepNumber);
  }

  getProgressPercentage(): number {
    return Math.round((this.currentStep / this.totalSteps) * 100);
  }

  private updateStepGlossary(): void {
    switch(this.currentStep) {
      case 1:
        this.currentStepGlossary = [
          {
            term: 'Lista Blanca',
            definition: 'Solo las personas con estas etiquetas pueden acceder al plan'
          },
          {
            term: 'Lista Negra',
            definition: 'Las personas con estas etiquetas NO pueden acceder al plan'
          },
          {
            term: 'Etiquetas',
            definition: 'Clasificaciones asignadas a personas para segmentación'
          },
          {
            term: 'Audiencia Estimada',
            definition: 'Número aproximado de personas que cumplen con los criterios'
          }
        ];
        break;

      case 2:
        this.currentStepGlossary = [
          {
            term: 'Tipo de Producto',
            definition: 'Categoría específica de producto/servicio al que aplica el plan'
          },
          {
            term: 'Producto Padre',
            definition: 'Categoría principal que contiene múltiples identificadores'
          },
          {
            term: 'Identificador',
            definition: 'Código específico del producto dentro de una categoría'
          },
          {
            term: 'Un Solo Producto',
            definition: 'Cada plan puede aplicar únicamente a un tipo de producto específico'
          }
        ];
        break;

      case 3:
        this.currentStepGlossary = [
          {
            term: 'Plan de Contado',
            definition: 'Pago único del 100% del valor. Sin cuotas ni financiamiento.'
          },
          {
            term: 'Plan a Crédito',
            definition: 'Financiamiento con pagos fraccionados en el tiempo.'
          },
          {
            term: 'Código del Plan',
            definition: 'Identificador único e inmutable del sistema para integraciones.'
          },
          {
            term: 'Prioridad',
            definition: 'Orden de sugerencia cuando un cliente es elegible para múltiples planes.'
          },
          {
            term: 'Multicompra',
            definition: 'Permite que un cliente use el mismo plan en múltiples ocasiones.'
          },
          {
            term: 'Estado Inactivo',
            definition: 'Plan creado pero no disponible para clientes. Recomendado para revisión.'
          },
          {
            term: 'Estado Activo',
            definition: 'Plan disponible y visible para clientes elegibles.'
          },
          {
            term: 'Fecha de Vigencia',
            definition: 'Período durante el cual el plan está disponible para uso.'
          }
        ];
        break;

      case 4:
        this.currentStepGlossary = [
          {
            term: 'Pago Inicial',
            definition: 'Cantidad que el cliente paga por adelantado antes de las cuotas'
          },
          {
            term: 'Porcentaje vs Monto',
            definition: 'Inicial calculada como % del total o cantidad fija en USD'
          },
          {
            term: 'Recalcular Cuotas',
            definition: 'Si activo, resta la inicial del monto total para calcular cuotas'
          },
          {
            term: 'Opciones de Cuotas',
            definition: 'Número de pagos que puede elegir el cliente (ej: 2,4,6)'
          },
          {
            term: 'Frecuencia',
            definition: 'Periodicidad de los pagos: semanal, quincenal, mensual'
          },
          {
            term: 'Cupo de Crédito',
            definition: 'Capacidad crediticia asignada al cliente en el sistema'
          },
          {
            term: 'Monto a Financiar',
            definition: 'Cantidad que se pagará en cuotas, puede incluir o excluir la inicial'
          },
          {
            term: 'Regla de Oro',
            definition: 'Inicial absoluta debe ser menor al monto mínimo de compra'
          }
        ];
        break;

      case 5:
        this.currentStepGlossary = [
          {
            term: 'Filtros Demográficos',
            definition: 'Restricciones basadas en características de la persona'
          },
          {
            term: 'Filtros Geográficos',
            definition: 'Restricciones basadas en ubicación o tienda'
          }
        ];
        break;

      case 6:
        this.currentStepGlossary = [
          {
            term: 'Pagaré Electrónico',
            definition: 'Documento legal digital que formaliza la deuda del cliente'
          },
          {
            term: 'Pagaré Físico',
            definition: 'Documento legal impreso que formaliza la deuda del cliente'
          },
          {
            term: 'Diferimiento de Cuotas',
            definition: 'Mover automáticamente las cuotas que caigan en fines de semana o feriados al siguiente día hábil'
          },
          {
            term: 'Configuración Legal',
            definition: 'Aspectos legales y administrativos del plan de crédito'
          }
        ];
        break;

      case 7:
        this.currentStepGlossary = [
          {
            term: 'Revisión Final',
            definition: 'Verificación de todos los parámetros antes de crear el plan'
          },
          {
            term: 'Términos y Condiciones',
            definition: 'Aceptación formal de la configuración del plan'
          }
        ];
        break;

      default:
        this.currentStepGlossary = [];
    }
  }

  saveAndExit(): void {
    if (this.isSaving) return;

    this.isSaving = true;

    try {
      // Generar JSON para guardar (formato interno)
      const wizardData = this.getWizardInternalJSON();

      // Aquí podrías guardar el progreso si fuera necesario
      console.log('💾 Guardando progreso:', wizardData);

      // Volver al dashboard
      this.router.navigate(['/console/plans']);
    } catch (error) {
      console.error('❌ Error al guardar:', error);
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    this.router.navigate(['/console/plans']);
  }

  async finalizePlan(): Promise<void> {
    if (this.isSaving) return;

    if (!this.step7Form.valid) {
      console.warn('⚠️ Formulario de finalización no válido');
      alert('Por favor acepta los términos y confirma los datos antes de continuar');
      return;
    }

    // Validar que todos los pasos requeridos estén completos
    const missingFields = this.getMissingRequiredFields();
    if (missingFields.length > 0) {
      console.warn('⚠️ Campos requeridos faltantes:', missingFields);
      alert(`Faltan campos requeridos: ${missingFields.join(', ')}`);
      return;
    }

    try {
      this.isSaving = true;

      // Log detallado ANTES de generar el JSON
      console.log('🔍 ESTADO ANTES DE GUARDAR:');
      console.log('  - selectedProposal:', this.selectedProposal);
      console.log('  - _selectedProposal:', this._selectedProposal);
      console.log('  - addedProducts:', this.addedProducts);
      console.log('  - addedProducts[0]?.insuranceProposal:', this.addedProducts[0]?.insuranceProposal);
      console.log('  - showInsuranceProposals:', this.showInsuranceProposals);
      console.log('  - insuranceProposals.length:', this.insuranceProposals.length);
      console.log('  - savedProposalState:', this.savedProposalState);

      // Generar JSON en formato de base de datos
      const planData = this.getDatabaseFormatJSON();

      console.log('👤 Usuario actualizador:', this.authService.sessionUser?._id);
      console.log('📅 Fecha actualización:', new Date());
      console.log('🔍 VERIFICANDO CAMPO DATE:');
      console.log('  - originalPlanData.date:', this.originalPlanData?.date);
      console.log('  - planData.date después de spread:', planData.date);
      console.log('📅 Campo updated_by.date:', planData.updated_by?.date);
      console.log('🔍 VERIFICANDO INSURANCE.PLAN:');
      console.log('  - planData.params.insurance:', planData.params.insurance);
      console.log('📤 Enviando plan actualizado:', planData);

      // Enviar al backend
      const response = await this.planService.updatePlan(planData);

      console.log('📡 Respuesta del servidor:', response);

      if (response && (response.success || response.res || response.data)) {
        console.log('✅ Plan actualizado exitosamente');
        alertifyjs.success('Plan actualizado exitosamente');
        this.router.navigate(['/console/plans']);
      } else {
        console.error('❌ Error al actualizar el plan:', response);
        
        // Extraer el mensaje de error específico del backend
        const backendMessage = response?.msg || 
                              response?.message || 
                              'Por favor intenta nuevamente.';
        
        alertifyjs.error(`Error al actualizar el plan: ${backendMessage}`);
      }
    } catch (error: any) {
      console.error('❌ Error al finalizar plan:', error);
      
      // Extraer el mensaje de error específico del backend
      const backendMessage = error?.error?.msg || 
                            error?.error?.message || 
                            error?.msg || 
                            error?.message || 
                            'Verifica tu conexión e intenta nuevamente.';
      
      alertifyjs.error(`Error al actualizar el plan: ${backendMessage}`);
    } finally {
      this.isSaving = false;
    }
  }

  // Métodos para generar JSONs (adaptados del componente original)
  getWizardInternalJSON(): any {
    return {
      // Metadatos del wizard
      _wizard: {
        currentStep: this.currentStep,
        completedSteps: this.completedSteps,
        enforceSequentialNavigation: this.enforceSequentialNavigation,
        totalSteps: this.totalSteps,
        selectedPlanType: this.getSelectedPlanType(),
        mode: 'edit',
        originalPlanCode: this.planCode
      },

      // Paso 1: Etiquetas/Códigos Dinámicos
      step1_tags: {
        filterMode: this.step1Form?.get('filterMode')?.value || null,
        selectedTags: this.step1Form?.get('selectedTags')?.value || [],
        isValid: this.validateStep1()
      },

      // Paso 2: Fees/Penalidades
      step2_fees: {
        whiteListFees: this.step2Form?.get('whiteListFees')?.value || [],
        blackListFees: this.step2Form?.get('blackListFees')?.value || [],
        isValid: this.validateStep2()
      },

      // Paso 3: Productos
      step3_products: {
        selectedProducts: this.step3Form?.get('selectedProducts')?.value || [],
        addedProductsCount: this.addedProducts.length,
        isValid: this.validateStep3()
      },

      // Paso 4: Datos Básicos
      step4_basic: {
        planType: this.step4Form?.get('planType')?.value || '',
        planName: this.step4Form?.get('planName')?.value || '',
        planCode: this.step4Form?.get('planCode')?.value || '',
        description: this.step4Form?.get('description')?.value || '',
        status: this.step4Form?.get('status')?.value || 'false',
        priority: this.step4Form?.get('priority')?.value ?? 0,
        icon: this.step4Form?.get('icon')?.value || 'fas fa-star',
        startDate: this.step4Form?.get('startDate')?.value || '',
        endDate: this.step4Form?.get('endDate')?.value || '',
        multicompra: this.step4Form?.get('multicompra')?.value || false,
        isValid: this.validateStep4()
      },

      // Resto de pasos similar al componente original...
      // (Se implementarían todos los pasos como en el wizard original)
    };
  }

  getDatabaseFormatJSON(): any {
    const planType = this.getSelectedPlanType();
    const isCredit = planType === 'credit' || planType === 'suscripcion';
    
    // Determinar el tipo para la base de datos: 'CONTADO', 'SUSCRIPCION', o 'CREDITO'
    let paramsType: string;
    if (planType === 'cash') {
      paramsType = 'CONTADO';
    } else if (planType === 'suscripcion') {
      paramsType = 'SUSCRIPCION';
    } else {
      paramsType = 'CREDITO';
    }

    // Preparar códigos dinámicos (etiquetas)
    let filterMode = this.step1Form?.get('filterMode')?.value;
    const selectedTags = this.step1Form?.get('selectedTags')?.value || [];

    // Si hay etiquetas seleccionadas pero no se eligió modo, usar whitelist por defecto
    if (!filterMode && selectedTags.length > 0) {
      filterMode = 'whitelist';
    }

    let dinamicCode = null;
    if (filterMode && selectedTags.length > 0) {
      dinamicCode = {
        person: {
          type: filterMode === 'whitelist' ? 'white_list' : 'black_list',
          list: selectedTags
        }
      };
    }

    // Obtener datos básicos
    const planName = this.step4Form?.get('planName')?.value || '';
    const planCode = this.step4Form?.get('planCode')?.value || '';
    const planDescription = this.step4Form?.get('description')?.value || '';
    const planIcon = this.step4Form?.get('icon')?.value || 'fas fa-star';
    const planStatus = this.step4Form?.get('status')?.value === 'true';

    // Mapear productos desde step2
    let productType = '';
    if (this.addedProducts.length > 0) {
      // Tomar el primer producto agregado (el sistema solo permite uno)
      productType = this.addedProducts[0].identifier?.code || '';
    }

    console.log('📦 Productos para guardar:', {
      addedProducts: this.addedProducts,
      productType: productType,
      addedProductsCount: this.addedProducts.length
    });

    // Mapear filtros de tienda desde el formulario
    const storeConfigEnabled = this.step6Form?.get('storeConfig.enabled')?.value || false;
    const storeFilterType = this.step6Form?.get('storeConfig.filterType')?.value || '';
    const selectedStores = this.step6Form?.get('storeConfig.selectedStores')?.value || [];

    console.log('🏪 Filtros de tienda para guardar:', {
      enabled: storeConfigEnabled,
      filterType: storeFilterType,
      selectedStores: selectedStores,
      storeIds: selectedStores.map((store: any) => store.id)
    });

    // Preparar estructura de filtros
    const generalFilters: any = {
      person: {
        audience: this.step6Form?.get('personConfig.personType')?.value?.toUpperCase() || 'NATURAL'
      }
    };

    // Agregar filtros de tienda si están habilitados
    if (storeConfigEnabled && selectedStores.length > 0) {
      generalFilters.invoices = {
        shop: {
          type: storeFilterType, // Usar directamente el valor del formulario (ya viene como 'white_list' o 'black_list')
          list: selectedStores.map((store: any) => store.id || store.code || store._id).filter(Boolean)
        }
      };
    }

    // Mapear filtros de productos del catálogo
    const productConfigEnabled = this.step6Form?.get('productConfig.enabled')?.value || false;
    const productFilterType = this.step6Form?.get('productConfig.filterType')?.value || '';
    const productMatchMode = this.step6Form?.get('productConfig.matchMode')?.value || 'any';
    const productAllowOthers = this.step6Form?.get('productConfig.allowOthers')?.value !== false;
    const selectedCatalogProducts = this.step6Form?.get('productConfig.selectedProducts')?.value || [];

    let productFilterObj: any = null;
    if (productConfigEnabled && selectedCatalogProducts.length > 0 && productFilterType) {
      productFilterObj = {
        type: productFilterType,
        match_mode: productMatchMode,
        allow_others: productAllowOthers,
        list: selectedCatalogProducts.map((p: any) => p.code).filter(Boolean)
      };
    }

    // Preparar el objeto principal (manteniendo estructura original)
    const { date: originalDate, ...originalDataWithoutDate } = this.originalPlanData;

    const planData: any = {
      ...originalDataWithoutDate, // Datos originales SIN el campo date
      name: planName,
      description: planDescription,
      status: planStatus,
      state: planStatus ? 'ACTIVE' : 'INACTIVE',

      // 📅 PRESERVAR FECHA ORIGINAL DE CREACIÓN
      date: originalDate, // Mantener fecha de creación intocada

      // 👤 CAMPO UPDATED_BY - Usuario actualizador
      updated_by: {
        user: this.authService.sessionUser?._id || null,
        date: new Date()
      },

      params: {
        ...this.originalPlanData.params,
        type: paramsType,
        icon: planIcon,
        priority: this.step4Form?.get('priority')?.value ?? 0, // Permitir 0 como valor válido (opcional para seguros)
        product_type: productType, // NUEVO: Mapear producto seleccionado
        // Incluir propuesta de seguros si existe
        insurance: (() => {
          console.log('🔍 EVALUANDO insurance (EDICIÓN):');
          console.log('  - addedProducts.length:', this.addedProducts.length);
          console.log('  - addedProducts[0]:', this.addedProducts[0]);
          console.log('  - addedProducts[0]?.insuranceProposal:', this.addedProducts[0]?.insuranceProposal);
          console.log('  - addedProducts[0]?.identifier?.insurance_product:', this.addedProducts[0]?.identifier?.insurance_product);
          console.log('  - selectedProposal:', this.selectedProposal);
          console.log('  - selectedProduct:', this.selectedProduct);
          console.log('  - selectedProduct?.insurance:', this.selectedProduct?.insurance);
          console.log('  - originalPlanData.params?.insurance:', this.originalPlanData.params?.insurance);
          
          // Obtener el código del plan (ID de la propuesta), nombre y prima
          let planCode = null;
          let proposalName = null;
          let mtPrima = null;
          
          if (this.addedProducts.length > 0 && this.addedProducts[0].insuranceProposal) {
            const proposal = this.addedProducts[0].insuranceProposal;
            // Si es un objeto con code, name y mtPrima
            if (typeof proposal === 'object' && proposal.code) {
              planCode = proposal.code;
              mtPrima = proposal.mtPrima;
              // Construir proposal_name con formato: "Nombre - Prima"
              proposalName = proposal.name && mtPrima ? `${proposal.name} - ${mtPrima}` : proposal.name;
            } else {
              // Si es solo un string (retrocompatibilidad)
              planCode = proposal;
            }
          } else if (this.selectedProposal) {
            planCode = this.selectedProposal;
            // Buscar el nombre y prima en la lista de propuestas
            const foundProposal = this.insuranceProposals.find(p => p.code === this.selectedProposal);
            if (foundProposal) {
              mtPrima = foundProposal.mtPrima;
              proposalName = foundProposal.name && mtPrima ? `${foundProposal.name} - ${mtPrima}` : foundProposal.name;
            }
          } else {
            // Preservar valores originales si no hay cambios
            planCode = this.originalPlanData.params?.insurance?.plan || null;
            proposalName = this.originalPlanData.params?.insurance?.proposal_name || null;
            mtPrima = this.originalPlanData.params?.insurance?.mtPrima || null;
          }
          
          // Obtener el código de la aseguradora del producto seleccionado
          const brokerageCode = this.addedProducts.length > 0 && this.addedProducts[0].product?.insurance?.insurer_code ?
            this.addedProducts[0].product.insurance.insurer_code :
            (this.selectedProduct?.insurance?.insurer_code || this.originalPlanData.params?.insurance?.brokerage || null);
          
          // Obtener el insurance_product del identificador seleccionado
          const insuranceProductCode = this.addedProducts.length > 0 && this.addedProducts[0].identifier?.insurance_product ?
            this.addedProducts[0].identifier.insurance_product :
            (this.selectedIdentifier?.insurance_product || this.originalPlanData.params?.insurance?.product || null);
          
          console.log('  - VALOR FINAL para insurance.plan:', planCode);
          console.log('  - VALOR FINAL para insurance.proposal_name:', proposalName);
          console.log('  - VALOR FINAL para insurance.mtPrima:', mtPrima);
          console.log('  - VALOR FINAL para insurance.product:', insuranceProductCode);
          console.log('  - VALOR FINAL para insurance.brokerage:', brokerageCode);
          
          return { 
            plan: planCode,
            proposal_name: proposalName,
            mtPrima: mtPrima,
            product: insuranceProductCode,
            brokerage: brokerageCode
          };
        })(),
        client_view: this.step7Form?.get('electronicPromissoryNote')?.value || false,
        generate_pagares: this.step7Form?.get('electronicPromissoryNote')?.value || false,
        generate_pagares_fisico: this.step7Form?.get('physicalPromissoryNote')?.value || false,
        multiple_invoices: {
          status: this.step4Form?.get('multicompra')?.value || false,
          count: 0
        },
        init_date: this.step4Form?.get('startDate')?.value || '',
        end_date: this.step4Form?.get('endDate')?.value || '',
        filters: (() => {
          const filters: any = {
            ...this.originalPlanData.params.filters,
            general: generalFilters,
            ...(productFilterObj ? { product_filter: productFilterObj } : {})
          };
          // Si hay tags, sobreescribir dinamic_code; si no hay tags, eliminar dinamic_code heredado del original
          if (dinamicCode) {
            filters.dinamic_code = dinamicCode;
          } else {
            delete filters.dinamic_code;
          }
          return filters;
        })(),
        weekday: this.step7Form?.get('allowWeekendsAndHolidays')?.value,
        client_visible: this.step7Form?.get('clientVisible')?.value ?? true // Visibilidad del plan al cliente
      }
    };
    console.log('GEMINI DEBUG (getDatabaseFormatJSON): allowWeekendsAndHolidays form value =', this.step6Form?.get('allowWeekendsAndHolidays')?.value);
    console.log('GEMINI DEBUG (getDatabaseFormatJSON): weekday sent to backend =', this.step6Form?.get('allowWeekendsAndHolidays')?.value);

    // Actualizar configuración de pago inicial si es crédito
    if (isCredit && this.step5Form?.get('initialPayment.required')?.value) {
      planData.initial = {
        status: true,
        type: this.step5Form?.get('initialPayment.calculationType')?.value || 'percentage',
        amount: this.step5Form?.get('initialPayment.value')?.value || 0,
        recalculate: this.step5Form?.get('initialPayment.recalculateInstallments')?.value || false
      };

      // Actualizar límites financieros
      planData.params.min_amount = this.step5Form?.get('financing.minPurchaseAmount')?.value || 0;
      planData.params.max_amount_base = this.step5Form?.get('financing.maxFinanceAmount')?.value || 0;
      planData.params.min_amount_base = this.calculateMinFinanceAmount() || 0;

      // Actualizar requisitos de cupo (solo para crédito completo, no para cuotas)
      if (this.isPlanTypeFullCredit()) {
        const minQuota = this.step5Form?.get('quotaRequirements.minQuota')?.value || 0;
        const maxQuota = this.step5Form?.get('quotaRequirements.maxQuota')?.value || 0;

        // Solo establecer cupos si tienen valores mayores a 0
        if (minQuota > 0 || maxQuota > 0) {
          planData.params.current_quota_min = minQuota;
          planData.params.current_quota_max = maxQuota;
        }
      }

      // GENERAR PAYMENT_TERMS - USANDO PAYMENT TERMS BUILDER
      planData.payment_terms = this.generatePaymentTermsFromBuilder();
      console.log('✅ Payment terms generados:', planData.payment_terms);
    } else {
      // Para CONTADO: misma configuración que creación (100% pago inicial)
      planData.initial = {
        status: true,
        type: 'percentage',
        amount: 100,
        recalculate: false
      };

      // Para planes de contado, payment_terms vacío (no enviar término único)
      planData.payment_terms = [];
    }

    return planData;
  }

  /**
   * Generar payment_terms desde los datos del formulario step4
   */
  private generatePaymentTermsFromForm(): any[] {
    console.log('🔄 Generando payment_terms desde formulario...');

    const installmentOptions = this.step5Form?.get('installments.options')?.value || '';
    const frequency = this.step5Form?.get('installments.frequency')?.value || 'mensual';
    const frequencyDays = this.step5Form?.get('installments.frequencyDays')?.value || 0;

    console.log('📊 Datos del formulario:', { installmentOptions, frequency, frequencyDays });

    if (!installmentOptions || installmentOptions.trim() === '') {
      console.log('⚠️ No hay opciones de cuotas, retornando array vacío');
      return [];
    }

    // Convertir string de opciones a array de números
    const installmentArray = installmentOptions
      .split(',')
      .map((opt: string) => parseInt(opt.trim()))
      .filter((num: number) => !isNaN(num) && num > 0)
      .sort((a: number, b: number) => a - b);

    console.log('📊 Opciones de cuotas procesadas:', installmentArray);

    if (installmentArray.length === 0) {
      console.log('⚠️ No hay opciones de cuotas válidas, retornando array vacío');
      return [];
    }

    // Generar payment_terms para cada opción de cuotas
    const paymentTerms = installmentArray.map((installments: number, index: number) => {
      const code = `${this.planCode}_${installments}`;
      const name = frequency === 'dias_continuos'
        ? `${installments} cuotas pagadas cada ${frequencyDays} días`
        : `${installments} cuotas pagadas ${this.mapFrequencyToSpanish(frequency)}`;

      return {
        code: code,
        frequency: {
          type: frequency,
          ...(frequency === 'dias_continuos' && { number: frequencyDays })
        },
        name: name,
        order: index,
        installments: installments
      };
    });

    console.log('✅ Payment terms generados:', paymentTerms);
    return paymentTerms;
  }

  /**
   * Mapear frecuencia a español para nombres de payment_terms
   */
  private mapFrequencyToSpanish(frequency: string): string {
    const frequencyMap: { [key: string]: string } = {
      'semanal': 'semanal',
      'quincenal': 'quincenal',
      'mensual': 'mensual',
      'weekly': 'semanal'
        };

    return frequencyMap[frequency] || 'mensual';
  }

  getSelectedPlanType(): string {
    // Usar getRawValue() para obtener el valor de un control deshabilitado
    return this.step4Form?.getRawValue().planType || 'cash';
  }

  isPlanTypeCredit(): boolean {
    const planType = this.getSelectedPlanType();
    // Tanto 'credit' como 'suscripcion' requieren configuración de cuotas
    return planType === 'credit' || planType === 'suscripcion';
  }

  isPlanTypeCash(): boolean {
    return this.getSelectedPlanType() === 'cash';
  }

  isPlanTypeFullCredit(): boolean {
    // Solo planes de tipo 'credit' (crédito completo) necesitan requisitos de cupo
    // Los planes de 'suscripcion' NO necesitan requisitos de cupo
    return this.getSelectedPlanType() === 'credit';
  }

  // Métodos de validación básicos
  private validateStep1(): boolean {
    console.log('🔍 Validating Step 1...');

    if (!this.step1Form) {
      console.log('❌ Step 1 form not initialized');
      return false;
    }

    const filterMode = this.step1Form.get('filterMode')?.value;
    const selectedTags = this.step1Form.get('selectedTags')?.value || [];

    console.log('📊 Step 1 values:', { filterMode, selectedTags });

    // Si no se selecciona modo de filtro, es válido (filtros opcionales)
    if (!filterMode) {
      console.log('✅ No filter mode selected - valid (optional)');
      return true;
    }

    // Si se selecciona lista blanca, debe tener al menos una etiqueta
    if (filterMode === 'whitelist' && selectedTags.length === 0) {
      console.log('❌ Whitelist selected but no tags provided');
      return false;
    }

    // Lista negra puede estar vacía (significa "no excluir a nadie")
    console.log('✅ Step 1 validation passed');
    return true;
  }

  private validateStep2(): boolean {
    // El paso 2 (Penalidades) es completamente opcional
    // No hay campos obligatorios en este paso
    return true;
  }

  private validateStep3(): boolean {
    // El paso 3 (Productos) - Debe haber al menos un producto agregado
    return this.addedProducts.length > 0;
  }

  private validateStep4(): boolean {
    if (!this.step4Form) return false;

    // Validar campos requeridos básicos del paso 4 (Datos Básicos)
    const planName = this.step4Form.get('planName')?.value;
    const planCode = this.step4Form.get('planCode')?.value;
    const planType = this.step4Form.get('planType')?.value;
    const startDate = this.step4Form.get('startDate')?.value;

    if (!planName || !planCode || !planType || !startDate) {
      return false;
    }

    // Validar formato del código
    const codePattern = /^[A-Z0-9_-]+$/i;
    if (!codePattern.test(planCode)) {
      return false;
    }

    // Validar fecha de fin si está presente
    const endDate = this.step4Form.get('endDate')?.value;
    if (endDate && new Date(endDate) <= new Date(startDate)) {
      return false;
    }

    return this.step4Form.valid;
  }

  private validateEndDate(control: any): { [key: string]: boolean } | null {
    const endDate = control.value;
    const startDate = this.step4Form?.get('startDate')?.value;

    if (endDate && startDate && new Date(endDate) <= new Date(startDate)) {
      return { invalidEndDate: true };
    }

    return null;
  }

  // Métodos para UI (copiados del componente original)
  getCurrentStepTitle(): string {
    const step = this.wizardSteps.find(s => s.id === this.currentStep);
    return step ? step.title : '';
  }

  getCurrentStepDescription(): string {
    const step = this.wizardSteps.find(s => s.id === this.currentStep);
    return step ? step.description : '';
  }

  getCurrentStepIcon(): string {
    const step = this.wizardSteps.find(s => s.id === this.currentStep);
    return step ? step.icon : 'fas fa-circle';
  }

  getCurrentStepIconClasses(): string {
    return 'bg-blue-100 text-blue-600';
  }

  getStepNavClasses(stepId: number): string {
    if (this.currentStep === stepId) {
      return 'bg-blue-50 border-blue-200 text-blue-700';
    } else if (this.isStepCompleted(stepId)) {
      return 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100';
    } else {
      return 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50';
    }
  }

  getStepIconClasses(stepId: number): string {
    if (this.currentStep === stepId) {
      return 'bg-blue-500 text-white';
    } else if (this.isStepCompleted(stepId)) {
      return 'bg-green-500 text-white';
    } else {
      return 'bg-gray-300 text-gray-600';
    }
  }

  getStepTitleClasses(stepId: number): string {
    if (this.currentStep === stepId) {
      return 'text-blue-900 font-semibold';
    } else if (this.isStepCompleted(stepId)) {
      return 'text-green-900 font-medium';
    } else {
      return 'text-gray-700';
    }
  }

  // Métodos para manejo de etiquetas, productos, etc. (se implementarían igual que en el original)
  async loadAvailableTags(): Promise<void> {
    try {
      this.tagsLoading = true;

      // Cargar etiquetas desde la API de códigos
      const codesResponse = await this.userService.getAllCodes();

      if (codesResponse?.data && Array.isArray(codesResponse.data)) {
        // Filtrar solo códigos con categoría "person" y extraer códigos únicos
        this.availableTags = codesResponse.data
          .filter((item: any) => item.category === 'person')
          .map((item: any) => item.code)
          .filter(Boolean);
        this.filteredTags = [...this.availableTags];
      } else {
        // Si no hay datos de la API, dejar arrays vacíos
        this.availableTags = [];
        this.filteredTags = [];
      }

    } catch (error) {
      console.error('Error loading tags:', error);
      // En caso de error, dejar arrays vacíos - no usar datos de ejemplo
      this.availableTags = [];
      this.filteredTags = [];
    } finally {
      this.tagsLoading = false;
    }
  }

  async loadAvailableProducts(): Promise<void> {
    try {
      this.productsLoading = true;

      // Cargar productos padre desde la API
      const productTypesResponse = await this.userService.getTypesProductsInvoices();

      if (productTypesResponse?.resp && Array.isArray(productTypesResponse.resp)) {
        // Procesar y organizar los productos padre
        this.availableProducts = productTypesResponse.resp.map((item: any) => ({
          code: item.params?.code || '',
          name: item.params?.name || item.params?.code || 'Sin nombre',
          description: item.params?.description || '',
          products: item.params?.products || [], // Identificadores hijos
          // Guardar información de seguros del producto
          insurance: {
            is_insurance_product: item.params?.insurance?.is_insurance_product || false,
            insurer_code: item.params?.insurance?.insurer_code || null
          }
        })).filter((product: any) => product.code); // Solo productos con código válido

        console.log('📦 Productos cargados con información de seguros:', this.availableProducts);
        this.filteredProducts = [...this.availableProducts];
      } else {
        this.availableProducts = [];
        this.filteredProducts = [];
      }

    } catch (error) {
      console.error('Error loading products:', error);
      this.availableProducts = [];
      this.filteredProducts = [];
    } finally {
      this.productsLoading = false;
    }
  }

  async loadAvailableStores(): Promise<void> {
    this.storesLoading = true;
    try {
      console.log('🏪 Cargando tiendas del sistema...');
      const response = await this.userService.getshops();
      console.log('🏪 Respuesta del API getshops():', response);

      let storesData: any[] = [];

      // Manejar diferentes estructuras de respuesta
      if (response) {
        if (response.res && Array.isArray(response.res)) {
          // Formato correcto: { res: [...] } - usado en otros componentes
          storesData = response.res;
          console.log('🏪 Formato con res detectado (correcto)');
        } else if (response.success && Array.isArray(response.data)) {
          // Formato: { success: true, data: [...] }
          storesData = response.data;
          console.log('🏪 Formato con success/data detectado');
        } else if (Array.isArray(response.data)) {
          // Formato: { data: [...] } sin success
          storesData = response.data;
          console.log('🏪 Formato solo con data detectado');
        } else if (Array.isArray(response)) {
          // Formato: directamente un array
          storesData = response;
          console.log('🏪 Formato array directo detectado');
        } else if (response.resp && Array.isArray(response.resp)) {
          // Formato: { resp: [...] } (común en otros endpoints)
          storesData = response.resp;
          console.log('🏪 Formato con resp detectado');
        }
      }

      if (storesData.length > 0) {
        console.log('🏪 Datos de tiendas encontrados:', storesData.length, 'tiendas');
        console.log('🏪 Primera tienda de ejemplo:', storesData[0]);

        // Mapear la respuesta del API al formato esperado por el componente
        this.availableStores = storesData.map((shop: any) => ({
          id: shop.code || shop.id || shop.shop_code,
          name: shop.name || shop.shop_name || shop.title || 'Sin nombre',
          category: shop.category || shop.type || 'Principal',
          city: shop.city || shop.location || shop.address || 'No especificada'
        })).filter(store => store.id); // Solo tiendas con ID válido

        console.log('🏪 Tiendas mapeadas:', this.availableStores.length);
        console.log('🏪 Primera tienda mapeada:', this.availableStores[0]);
      } else {
        console.warn('🏪 No se encontraron tiendas en la respuesta');
        console.warn('🏪 Estructura de respuesta:', {
          hasResponse: !!response,
          hasRes: !!response?.res,
          hasSuccess: response?.success,
          hasData: !!response?.data,
          hasResp: !!response?.resp,
          isResArray: Array.isArray(response?.res),
          isDataArray: Array.isArray(response?.data),
          isRespArray: Array.isArray(response?.resp),
          isDirectArray: Array.isArray(response),
          responseKeys: response ? Object.keys(response) : 'No response',
          responseType: typeof response
        });
        this.availableStores = [];
      }

      this.filteredStores = [...this.availableStores];
    } catch (error) {
      console.error('🏪 Error loading stores:', error);
      this.availableStores = [];
      this.filteredStores = [];
    } finally {
      this.storesLoading = false;
    }
  }

  // Métodos para alternar modo de navegación
  toggleNavigationMode(): void {
    this.enforceSequentialNavigation = !this.enforceSequentialNavigation;
    console.log('🔄 Modo de navegación:', this.enforceSequentialNavigation ? 'Secuencial' : 'Libre');
  }

  // Métodos para manejo de etiquetas
  onTagSearch(event: any): void {
    const input = event.target as HTMLInputElement;
    this.tagSearchTerm = input.value;
    this.filterTags();
    this.showTagDropdown = this.tagSearchTerm.length > 0 && this.filteredTags.length > 0;
  }

  onTagSearchFocus(): void {
    if (this.tagSearchTerm.length > 0 && this.filteredTags.length > 0) {
      this.showTagDropdown = true;
    } else if (this.tagSearchTerm.length === 0 && this.availableTags.length > 0) {
      this.filteredTags = [...this.availableTags];
      const selectedTags = this.step1Form.get('selectedTags')?.value || [];
      this.filteredTags = this.filteredTags.filter(tag => !selectedTags.includes(tag));
      this.showTagDropdown = this.filteredTags.length > 0;
    }
  }

  hideTagDropdown(): void {
    // Delay para permitir que el click en una etiqueta se procese primero
    setTimeout(() => {
      this.showTagDropdown = false;
    }, 150);
  }

  selectTag(tag: string): void {
    const selectedTags = this.step1Form.get('selectedTags')?.value || [];

    if (!selectedTags.includes(tag)) {
      const updatedTags = [...selectedTags, tag];
      this.step1Form.patchValue({ selectedTags: updatedTags });
    }

    // Limpiar búsqueda y cerrar dropdown
    this.clearTagSearch();
  }

  removeTag(tagToRemove: string): void {
    const selectedTags = this.step1Form.get('selectedTags')?.value || [];
    const updatedTags = selectedTags.filter((tag: string) => tag !== tagToRemove);
    this.step1Form.patchValue({ selectedTags: updatedTags });

    // Actualizar filtros para mostrar la etiqueta removida nuevamente
    this.filterTags();
  }

  clearTagSearch(): void {
    this.tagSearchTerm = '';
    this.showTagDropdown = false;
    this.filteredTags = [...this.availableTags];

    // Limpiar el input
    const searchInput = document.querySelector('input[placeholder="Buscar etiquetas..."]') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }
  }

  filterTags(): void {
    if (!this.tagSearchTerm.trim()) {
      this.filteredTags = [...this.availableTags];
      this.showTagDropdown = false;
      return;
    }

    const searchTerm = this.tagSearchTerm.toLowerCase();
    const selectedTags = this.step1Form.get('selectedTags')?.value || [];

    this.filteredTags = this.availableTags.filter(tag =>
      tag.toLowerCase().includes(searchTerm) &&
      !selectedTags.includes(tag)
    );
  }

  private filterAvailableTags(): void {
    this.filterTags();
  }

  getEstimatedAudience(): number {
    const selectedTags = this.step1Form.get('selectedTags')?.value || [];
    const filterMode = this.step1Form.get('filterMode')?.value;

    if (!filterMode) return 0;

    // Mock calculation - en producción esto vendría de una API
    const baseUsers = 2000;

    if (filterMode === 'whitelist') {
      // Lista blanca: menos usuarios mientras más etiquetas específicas
      const reductionFactor = Math.max(0.1, 1 - (selectedTags.length * 0.15));
      return Math.floor(baseUsers * reductionFactor);
    } else {
      // Lista negra: más usuarios mientras más etiquetas excluidas
      const reductionFactor = Math.min(0.9, selectedTags.length * 0.1);
      return Math.floor(baseUsers * (1 - reductionFactor));
    }
  }

  // ==================== MÉTODOS PARA FEES (PASO 2) ====================

  async loadAvailableFees(): Promise<void> {
    try {
      this.feesLoading = true;
      console.log('🔄 Cargando fees desde la API...');
      const response = await this.userService.getAllFees();
      console.log('📦 Respuesta de fees:', response);
      
      if (response?.success && Array.isArray(response.data)) {
        // Formato: { success: true, data: [...] }
        this.availableFees = response.data; // Mantener estructura original con code_concept
        this.filteredFees = [...this.availableFees];
        console.log('✅ Fees cargados (formato success/data):', this.availableFees.length);
        console.log('📋 Primer fee de ejemplo:', this.availableFees[0]);
      } else if (response && Array.isArray(response)) {
        // La respuesta es un array directo
        this.availableFees = response; // Mantener estructura original
        this.filteredFees = [...this.availableFees];
        console.log('✅ Fees cargados (formato array directo):', this.availableFees.length);
      } else if (response?.resp && Array.isArray(response.resp)) {
        // Formato alternativo con resp
        this.availableFees = response.resp; // Mantener estructura original
        this.filteredFees = [...this.availableFees];
        console.log('✅ Fees cargados (formato resp):', this.availableFees.length);
      } else {
        console.warn('⚠️ Respuesta de fees en formato inesperado');
        console.warn('Estructura recibida:', response);
        this.availableFees = [];
        this.filteredFees = [];
      }
    } catch (error) {
      console.error('❌ Error loading fees:', error);
      alertifyjs.error('Error al cargar penalidades');
      this.availableFees = [];
      this.filteredFees = [];
    } finally {
      this.feesLoading = false;
    }
  }

  onFeeSearch(event: any): void {
    const input = event.target as HTMLInputElement;
    this.feeSearchTerm = input.value;
    this.filterFees();
    this.showFeeDropdown = this.feeSearchTerm.length > 0 && this.filteredFees.length > 0;
  }

  onFeeSearchFocus(): void {
    console.log('🔍 Focus en buscador de fees');
    console.log('📊 Fees disponibles:', this.availableFees.length);
    console.log('📊 Fees filtrados:', this.filteredFees.length);
    
    if (this.feeSearchTerm.length > 0) {
      // Si hay texto, filtrar y mostrar
      this.filterFees();
      this.showFeeDropdown = true;
    } else if (this.availableFees.length > 0) {
      // Si no hay texto, mostrar todos los fees disponibles
      // Filtrar los que ya están en las listas
      const whiteList = this.step2Form.get('whiteListFees')?.value || [];
      const blackList = this.step2Form.get('blackListFees')?.value || [];
      const alreadyAdded = [...whiteList, ...blackList];
      
      this.filteredFees = this.availableFees.filter(fee => 
        !alreadyAdded.includes(fee.code_concept)
      );
      
      this.showFeeDropdown = true;
      console.log('✅ Mostrando dropdown con', this.filteredFees.length, 'fees');
    } else {
      console.warn('⚠️ No hay fees disponibles');
      this.showFeeDropdown = true; // Mostrar para que se vea el mensaje de "no hay fees"
    }
  }

  hideFeeDropdown(): void {
    setTimeout(() => {
      this.showFeeDropdown = false;
    }, 150);
  }

  selectFeeForAdding(fee: any): void {
    this.selectedFeeToAdd = fee;
    this.clearFeeSearch();
  }

  clearSelectedFee(): void {
    this.selectedFeeToAdd = null;
  }

  async addFeeToList(): Promise<void> {
    if (!this.selectedFeeToAdd) {
      alertifyjs.warning('Selecciona una penalidad primero');
      return;
    }

    const feeCode = this.selectedFeeToAdd.code_concept; // Usar code_concept
    const targetList = this.selectedListType === 'white_list' ? 'whiteListFees' : 'blackListFees';
    const otherList = this.selectedListType === 'white_list' ? 'blackListFees' : 'whiteListFees';
    
    const currentList = this.step2Form.get(targetList)?.value || [];
    const otherListValues = this.step2Form.get(otherList)?.value || [];

    // Verificar si ya está en la lista actual
    if (currentList.includes(feeCode)) {
      alertifyjs.warning('Esta penalidad ya está en la lista');
      return;
    }

    // Si está en la otra lista, removerlo primero
    let updatedOtherList = otherListValues;
    if (otherListValues.includes(feeCode)) {
      updatedOtherList = otherListValues.filter((code: string) => code !== feeCode);
      this.step2Form.get(otherList)?.patchValue(updatedOtherList);
    }

    // Agregar a la lista seleccionada
    const updatedList = [...currentList, feeCode];
    this.step2Form.get(targetList)?.patchValue(updatedList);

    // Actualizar en el backend usando el nuevo endpoint específico
    try {
      const whiteListFees = this.selectedListType === 'white_list' ? updatedList : updatedOtherList;
      const blackListFees = this.selectedListType === 'black_list' ? updatedList : updatedOtherList;

      console.log('📤 Actualizando fees en el plan:', this.originalPlanData.code);
      const response = await this.planService.updatePlanFees(
        this.originalPlanData.code,
        whiteListFees,
        blackListFees
      );
      
      if (response?.success) {
        alertifyjs.success(`Penalidad agregada a ${this.selectedListType === 'white_list' ? 'Lista Blanca' : 'Lista Negra'}`);
        console.log('✅ Fees actualizados exitosamente');
      }
    } catch (error) {
      console.error('❌ Error al actualizar fees:', error);
      alertifyjs.error('Error al guardar la penalidad');
      // Revertir cambios en caso de error
      this.step2Form.get(targetList)?.patchValue(currentList);
      if (otherListValues.includes(feeCode)) {
        this.step2Form.get(otherList)?.patchValue(otherListValues);
      }
    }
    
    // Limpiar selección
    this.selectedFeeToAdd = null;
  }

  async removeFeeFromList(feeCode: string, listType: 'white_list' | 'black_list'): Promise<void> {
    const targetList = listType === 'white_list' ? 'whiteListFees' : 'blackListFees';
    const currentList = this.step2Form.get(targetList)?.value || [];
    const updatedList = currentList.filter((code: string) => code !== feeCode);
    
    // Actualizar en el formulario
    this.step2Form.get(targetList)?.patchValue(updatedList);
    
    // Actualizar en el backend usando el nuevo endpoint específico
    try {
      const whiteList = listType === 'white_list' ? updatedList : (this.step2Form.get('whiteListFees')?.value || []);
      const blackList = listType === 'black_list' ? updatedList : (this.step2Form.get('blackListFees')?.value || []);

      console.log('📤 Eliminando fee del plan:', this.originalPlanData.code);
      const response = await this.planService.updatePlanFees(
        this.originalPlanData.code,
        whiteList,
        blackList
      );
      
      if (response?.success) {
        alertifyjs.success('Penalidad eliminada de la lista');
        console.log('✅ Fee eliminado exitosamente');
      }
    } catch (error) {
      console.error('❌ Error al eliminar fee:', error);
      alertifyjs.error('Error al eliminar la penalidad');
      // Revertir cambios en caso de error
      this.step2Form.get(targetList)?.patchValue(currentList);
    }
  }

  clearFeeSearch(): void {
    this.feeSearchTerm = '';
    this.showFeeDropdown = false;
    this.filteredFees = [...this.availableFees];

    const searchInput = document.querySelector('input[placeholder="Buscar penalidades..."]') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }
  }

  // Método helper para obtener el nombre de un fee por su code_concept
  getFeeName(code_concept: string): string {
    const fee = this.availableFees.find(f => f.code_concept === code_concept);
    return fee ? fee.name : code_concept;
  }

  filterFees(): void {
    if (!this.feeSearchTerm.trim()) {
      this.filteredFees = [...this.availableFees];
      this.showFeeDropdown = false;
      return;
    }

    const searchTerm = this.feeSearchTerm.toLowerCase();
    const whiteList = this.step2Form.get('whiteListFees')?.value || [];
    const blackList = this.step2Form.get('blackListFees')?.value || [];
    const alreadyAdded = [...whiteList, ...blackList];

    this.filteredFees = this.availableFees.filter(fee =>
      (fee.code_concept?.toLowerCase().includes(searchTerm) || 
       fee.name?.toLowerCase().includes(searchTerm)) &&
      !alreadyAdded.includes(fee.code_concept)
    );
  }

  // ==================== FIN MÉTODOS FEES ====================

  onProductSearch(event: any): void {
    const input = event.target as HTMLInputElement;
    this.productSearchTerm = input.value;
    this.filterProducts();
    this.showProductDropdown = this.productSearchTerm.length > 0 && this.filteredProducts.length > 0;
  }

  onProductSearchFocus(): void {
    if (this.productSearchTerm.length > 0 && this.filteredProducts.length > 0) {
      this.showProductDropdown = true;
    } else if (this.productSearchTerm.length === 0 && this.availableProducts.length > 0) {
      this.filteredProducts = [...this.availableProducts];
      this.showProductDropdown = this.filteredProducts.length > 0;
    }
  }

  hideProductDropdown(): void {
    // Delay para permitir que el click en un producto se procese primero
    setTimeout(() => {
      this.showProductDropdown = false;
    }, 150);
  }

  selectProduct(product: any): void {
    this.selectedProduct = product;
    this.productSearchTerm = product.name;
    this.showProductDropdown = false;

    // Cargar identificadores del producto seleccionado
    this.loadProductIdentifiers(product);

    // Limpiar selección de identificador anterior
    this.selectedIdentifier = null;
    this.identifierSearchTerm = '';
    this.showIdentifierDropdown = false;

    // NOTA: Ya no verificamos seguros aquí, lo haremos cuando se seleccione el identificador
    this.resetInsuranceProposals();

    // Limpiar estado guardado del producto anterior
    if (this.selectedProduct && this.selectedIdentifier) {
      const oldProductKey = `${this.selectedProduct.code}_${this.selectedIdentifier.code}`;
      delete this.savedProposalState[oldProductKey];
      console.log(`🗑️ Estado limpiado para producto anterior: ${oldProductKey}`);
    }
  }

  private filterAvailableProducts(): void {
    this.filterProducts();
  }

  filterProducts(): void {
    if (!this.productSearchTerm.trim()) {
      this.filteredProducts = [...this.availableProducts];
      this.showProductDropdown = false;
      return;
    }

    const searchTerm = this.productSearchTerm.toLowerCase();
    this.filteredProducts = this.availableProducts.filter(product =>
      product.name.toLowerCase().includes(searchTerm) ||
      product.code.toLowerCase().includes(searchTerm) ||
      (product.description && product.description.toLowerCase().includes(searchTerm))
    );
  }

  loadProductIdentifiers(product: any): void {
    this.identifiersLoading = true;

    // Los identificadores están en product.products
    if (product.products && Array.isArray(product.products)) {
      this.availableIdentifiers = product.products.map((identifier: any) => ({
        code: identifier.code || '',
        name: identifier.name || identifier.code || 'Sin nombre',
        description: identifier.description || '',
        insurance_product: identifier.insurance_product || null // IMPORTANTE: Campo para productos de seguros
      })).filter((identifier: any) => identifier.code);
      
      console.log('📦 Identificadores cargados:', this.availableIdentifiers);
    } else {
      this.availableIdentifiers = [];
    }

    this.filteredIdentifiers = [...this.availableIdentifiers];
    this.identifiersLoading = false;
  }

  private async loadIdentifiersForProduct(product: any): Promise<void> {
    this.loadProductIdentifiers(product);
  }

  onIdentifierSearch(event: any): void {
    const input = event.target as HTMLInputElement;
    this.identifierSearchTerm = input.value;
    this.filterIdentifiers();
    this.showIdentifierDropdown = this.identifierSearchTerm.length > 0 && this.filteredIdentifiers.length > 0;
  }

  onIdentifierSearchFocus(): void {
    if (this.identifierSearchTerm.length > 0 && this.filteredIdentifiers.length > 0) {
      this.showIdentifierDropdown = true;
    } else if (this.identifierSearchTerm.length === 0 && this.availableIdentifiers.length > 0) {
      this.filteredIdentifiers = [...this.availableIdentifiers];
      this.showIdentifierDropdown = this.filteredIdentifiers.length > 0;
    }
  }

  hideIdentifierDropdown(): void {
    // Delay para permitir que el click en un identificador se procese primero
    setTimeout(() => {
      this.showIdentifierDropdown = false;
    }, 150);
  }

  selectIdentifier(identifier: any): void {
    console.log('🎯 IDENTIFICADOR SELECCIONADO:', identifier);
    console.log('🎯 insurance_product del identificador:', identifier.insurance_product);
    
    this.selectedIdentifier = identifier;
    this.identifierSearchTerm = identifier.name;
    this.showIdentifierDropdown = false;

    // Verificar si el producto seleccionado es de seguros y cargar propuestas
    // Solo cuando tenemos tanto producto como identificador seleccionados
    if (this.selectedProduct && this.selectedIdentifier) {
      this.checkAndLoadInsuranceProposalsAfterIdentifierSelection();
    }
  }

  private filterAvailableIdentifiers(): void {
    this.filterIdentifiers();
  }

  filterIdentifiers(): void {
    if (!this.identifierSearchTerm.trim()) {
      this.filteredIdentifiers = [...this.availableIdentifiers];
      this.showIdentifierDropdown = false;
      return;
    }

    const searchTerm = this.identifierSearchTerm.toLowerCase();
    this.filteredIdentifiers = this.availableIdentifiers.filter(identifier =>
      identifier.name.toLowerCase().includes(searchTerm) ||
      identifier.code.toLowerCase().includes(searchTerm) ||
      (identifier.description && identifier.description.toLowerCase().includes(searchTerm))
    );
  }

  addProductToList(): void {
    if (!this.selectedProduct || !this.selectedIdentifier) {
      return;
    }

    // Verificar que no esté ya agregado
    const existingProduct = this.addedProducts.find(item =>
      item.product.code === this.selectedProduct.code &&
      item.identifier.code === this.selectedIdentifier.code
    );

    if (existingProduct) {
      alert('Este producto ya fue agregado al plan');
      return;
    }

    // Buscar el objeto completo de la propuesta seleccionada
    let insuranceProposalData = null;
    if (this.selectedProposal && this.insuranceProposals.length > 0) {
      const foundProposal = this.insuranceProposals.find(p => p.code === this.selectedProposal);
      if (foundProposal) {
        insuranceProposalData = {
          code: foundProposal.code,
          name: foundProposal.name,
          mtPrima: foundProposal.mtPrima
        };
        console.log('✅ Propuesta encontrada:', insuranceProposalData);
      }
    }

    // Agregar el producto a la lista
    const newProductItem = {
      product: { ...this.selectedProduct },
      identifier: { ...this.selectedIdentifier },
      insuranceProposal: insuranceProposalData // Incluir objeto completo de propuesta si existe
    };

    console.log('🔍 DEBUG - Agregando producto con propuesta:', {
      productCode: this.selectedProduct?.code,
      identifierCode: this.selectedIdentifier?.code,
      selectedProposal: this.selectedProposal,
      _selectedProposal: this._selectedProposal,
      showInsuranceProposals: this.showInsuranceProposals,
      insuranceProposalsLength: this.insuranceProposals.length,
      savedProposalState: this.savedProposalState,
      insuranceProposalData: insuranceProposalData,
      newProductItem: newProductItem
    });

    this.addedProducts.push(newProductItem);
    
    console.log('✅ Producto agregado. addedProducts:', this.addedProducts);

    // Actualizar validaciones de prioridad según tipo de producto
    this.updatePriorityValidation();

    // Actualizar el formulario (paso 3 es Productos)
    const identifierCodes = this.addedProducts.map(item => item.identifier.code);
    this.step3Form.patchValue({ selectedProducts: identifierCodes });

    // CAMBIO: NO limpiar la selección para mantener visible el select de propuestas
    // Solo limpiar los campos de búsqueda y dropdowns
    this.productSearchTerm = '';
    this.identifierSearchTerm = '';
    this.showProductDropdown = false;
    this.showIdentifierDropdown = false;
    
    console.log('✅ Selección mantenida para seguros. selectedProposal:', this.selectedProposal);
  }

  canAddProduct(): boolean {
    return !!(this.selectedProduct && this.selectedIdentifier);
  }

  removeProductFromList(index: number): void {
    if (index >= 0 && index < this.addedProducts.length) {
      this.addedProducts.splice(index, 1);

      // Actualizar validaciones de prioridad
      this.updatePriorityValidation();

      // Actualizar el formulario (paso 3 es Productos)
      const identifierCodes = this.addedProducts.map(item => item.identifier.code);
      this.step3Form.patchValue({ selectedProducts: identifierCodes });
    }
  }

  clearProductSelection(): void {
    this.selectedProduct = null;
    this.selectedIdentifier = null;
    this.productSearchTerm = '';
    this.identifierSearchTerm = '';
    this.showProductDropdown = false;
    this.showIdentifierDropdown = false;
    this.availableIdentifiers = [];
    this.filteredIdentifiers = [];

    // Limpiar configuración de seguros cuando se limpia la selección completa
    this.resetInsuranceProposals();

    // También limpiar el estado guardado
    this.savedProposalState = {};
    console.log('🗑️ Estado guardado limpiado al limpiar selección completa');
  }

  getAddedProductsText(): string {
    if (this.addedProducts.length === 0) {
      return 'Ningún producto seleccionado';
    }

    return this.addedProducts.map(item =>
      `${item.product.name} - ${item.identifier.name}`
    ).join(', ');
  }

  hasProductSelected(): boolean {
    return !!(this.selectedProduct && this.selectedIdentifier);
  }

  getSelectedProductInfo(): any {
    if (!this.selectedProduct || !this.selectedIdentifier) {
      return null;
    }
    return {
      product: this.selectedProduct,
      identifier: this.selectedIdentifier
    };
  }

  // Métodos para manejo de tipo de plan
  onPlanTypeChange(): void {
    const planType = this.step4Form.get('planType')?.value;
    console.log('📋 Tipo de plan cambiado a:', planType);

    if (planType === 'cash') {
      this.configureCashPlan();

      // Si estamos en el paso 5 (Datos Generales) y cambiamos a contado, navegar al siguiente paso
      if (this.currentStep === 5) {
        console.log('⏭️ Saltando paso 5 (Datos Generales) para plan de contado');
        this.currentStep = 6;
        this.updateStepGlossary();
      }
    } else if (planType === 'credit' || planType === 'suscripcion') {
      // Tanto 'credit' como 'suscripcion' requieren configuración de cuotas
      this.configureCreditPlan();
    }

    // Actualizar validaciones según el tipo
    this.updateStepValidations();
  }

  private configureCashPlan(): void {
    console.log('💰 Configurando plan de contado...');

    // Limpiar y deshabilitar campos específicos de crédito en step4Form
    this.step4Form.patchValue({
      initialPayment: {
        required: false,
        calculationType: 'percentage',
        value: 0,
        recalculateInstallments: false
      },
      financing: {
        minPurchaseAmount: 0,
        maxFinanceAmount: 0
      },
      installments: {
        options: '',
        frequency: 'mensual'
      },
      quotaRequirements: {
        minQuota: 0,
        maxQuota: 0
      }
    });

    // Deshabilitar controles de crédito
    this.step5Form.get('initialPayment')?.disable();
    this.step5Form.get('financing')?.disable();
    this.step5Form.get('installments')?.disable();
    this.step5Form.get('quotaRequirements')?.disable();

    // Limpiar validaciones de crédito
    this.step5Form.get('financing.minPurchaseAmount')?.clearValidators();
    this.step5Form.get('financing.maxFinanceAmount')?.clearValidators();
    this.step5Form.get('installments.options')?.clearValidators();

    this.step5Form.get('financing.minPurchaseAmount')?.updateValueAndValidity();
    this.step5Form.get('financing.maxFinanceAmount')?.updateValueAndValidity();
    this.step5Form.get('installments.options')?.updateValueAndValidity();

    console.log('✅ Plan de contado configurado');
  }

  private configureCreditPlan(): void {
    console.log('🏦 Configurando plan de crédito...');

    console.log('📋 Valores ANTES de habilitar controles:', {
      initialPayment: this.step5Form.get('initialPayment')?.value,
      financing: this.step5Form.get('financing')?.value,
      installments: this.step5Form.get('installments')?.value,
      quotaRequirements: this.step5Form.get('quotaRequirements')?.value
    });

    // Habilitar controles de crédito SIN sobrescribir valores
    this.step5Form.get('initialPayment')?.enable();
    this.step5Form.get('financing')?.enable();
    this.step5Form.get('installments')?.enable();
    this.step5Form.get('quotaRequirements')?.enable();

    // Establecer validaciones de crédito
    this.step5Form.get('financing.minPurchaseAmount')?.setValidators([Validators.required, Validators.min(0.01)]);
    this.step5Form.get('financing.maxFinanceAmount')?.setValidators([Validators.required, Validators.min(0.01)]);
    this.step5Form.get('installments.options')?.setValidators([Validators.required, Validators.pattern(/^[0-9]+(,[0-9]+)*$/)]);

    this.step5Form.get('financing.minPurchaseAmount')?.updateValueAndValidity();
    this.step5Form.get('financing.maxFinanceAmount')?.updateValueAndValidity();
    this.step5Form.get('installments.options')?.updateValueAndValidity();

    console.log('📋 Valores DESPUÉS de habilitar controles:', {
      initialPayment: this.step5Form.get('initialPayment')?.value,
      financing: this.step5Form.get('financing')?.value,
      installments: this.step5Form.get('installments')?.value,
      quotaRequirements: this.step5Form.get('quotaRequirements')?.value
    });

    console.log('�� Estado de validación de campos requeridos:', {
      minPurchaseAmountValid: this.step5Form.get('financing.minPurchaseAmount')?.valid,
      maxFinanceAmountValid: this.step5Form.get('financing.maxFinanceAmount')?.valid,
      installmentOptionsValid: this.step5Form.get('installments.options')?.valid,
      step4FormValid: this.step4Form.valid
    });

    console.log('✅ Plan de crédito configurado');
  }

  private updateStepValidations(): void {
    // Actualizar el estado de completado de los pasos después de cambiar el tipo de plan
    this.updateStepCompletion();
  }

  // Método para manejo de estado del plan
  onStatusToggle(event: any): void {
    const isActive = event.target.checked;
    this.step4Form.get('status')?.setValue(isActive ? 'true' : 'false');
    console.log('🔄 Estado del plan:', isActive ? 'Activo' : 'Inactivo');
  }

  // Método para manejo de código del plan
  onPlanCodeChange(event: any): void {
    const code = event.target.value;
    // Convertir a mayúsculas y remover caracteres no permitidos
    const cleanCode = code.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
    if (cleanCode !== code) {
      this.step4Form.get('planCode')?.setValue(cleanCode);
    }
  }

  // Método para obtener resumen del plan
  getPlanSummary(): any {
    const planType = this.getSelectedPlanType();
    const isCredit = planType === 'credit' || planType === 'suscripcion';

    // Mapear tipo de plan para el resumen
    let tipoTexto: string;
    if (planType === 'cash') {
      tipoTexto = 'Contado';
    } else if (planType === 'suscripcion') {
      tipoTexto = 'Suscripción';
    } else {
      tipoTexto = 'Crédito';
    }

    const summary: any = {
      name: this.step4Form?.get('planName')?.value || 'N/A',
      code: this.step4Form?.get('planCode')?.value || 'N/A',
      type: tipoTexto,
      status: this.step4Form?.get('status')?.value === 'true' ? 'Activo' : 'Inactivo',
      productsCount: this.addedProducts.length,
      hasPersonFilters: this.arePersonFiltersEnabled(),
      hasStoreFilters: this.areStoreFiltersEnabled()
    };

    if (isCredit) {
      summary.minPurchaseAmount = this.step5Form?.get('financing.minPurchaseAmount')?.value || 0;
      summary.maxFinanceAmount = this.step5Form?.get('financing.maxFinanceAmount')?.value || 0;
      summary.installmentOptions = this.getInstallmentOptions();
    }

    return summary;
  }

  private isAllStepsValid(): boolean {
    return this.step1Form.valid &&
           this.step2Form.valid &&
           this.step3Form.valid &&
           (this.isPlanTypeCash() || this.step4Form.valid) &&
           this.step5Form.valid &&
           this.step6Form.valid;
  }

  // Métodos adicionales para el paso 4 (Configuración de Crédito)
  onFinancingFieldChange(): void {
    // Recalcular valores dependientes cuando cambian los campos de financiamiento
    const minAmount = this.step5Form.get('financing.minPurchaseAmount')?.value || 0;
    const maxAmount = this.step5Form.get('financing.maxFinanceAmount')?.value || 0;

    if (minAmount > maxAmount && maxAmount > 0) {
      this.step5Form.get('financing.maxFinanceAmount')?.setValue(minAmount);
    }
    
    // Actualizar validadores del campo 'value' según el estado de 'required'
    const initialRequired = this.step5Form.get('initialPayment.required')?.value;
    const valueControl = this.step5Form.get('initialPayment.value');
    
    if (valueControl) {
      if (initialRequired) {
        // Si el pago inicial es requerido, hacer el campo 'value' obligatorio
        valueControl.setValidators([Validators.required, Validators.min(0)]);
      } else {
        // Si el pago inicial no es requerido, el campo 'value' es opcional
        valueControl.setValidators([Validators.min(0)]);
      }
      valueControl.updateValueAndValidity();
    }
  }

  calculateMinFinanceAmount(): number {
    const minPurchaseAmount = this.calculateMinPurchaseAmount();
    const initialRequired = this.step5Form?.get('initialPayment.required')?.value || false;

    if (!initialRequired) {
      return minPurchaseAmount;
    }

    const calculationType = this.step5Form?.get('initialPayment.calculationType')?.value || 'percentage';
    const initialValue = this.step5Form?.get('initialPayment.value')?.value || 0;
    const recalculate = this.step5Form?.get('initialPayment.recalculateInstallments')?.value || false;

    if (!recalculate) {
      return minPurchaseAmount;
    }

    if (calculationType === 'percentage') {
      const percentageAsDecimal = initialValue / 100;
      return minPurchaseAmount * (1 - percentageAsDecimal);
    } else {
      return Math.max(0, minPurchaseAmount - initialValue);
    }
  }

  getInstallmentOptions(): number[] {
    const optionsString = this.step5Form.get('installments.options')?.value || '';
    if (!optionsString.trim()) {
      return [];
    }

    return optionsString
      .split(',')
      .map((option: string) => parseInt(option.trim()))
      .filter((option: number) => !isNaN(option) && option > 0)
      .sort((a: number, b: number) => a - b);
  }

  getFrequencyLabel(): string {
    const frequency = this.step5Form.get('installments.frequency')?.value;
    switch (frequency) {
      case 'weekly':
        return 'Semanal';
      case 'quincenal':
        return 'Quincenal';
      case 'mensual':
        return 'Mensual';
      default:
        return 'No definido';
    }
  }

  // Métodos para el paso 5 (Filtros)
  arePersonFiltersEnabled(): boolean {
    return this.step6Form.get('personConfig.enabled')?.value || false;
  }

  areStoreFiltersEnabled(): boolean {
    return this.step6Form.get('storeConfig.enabled')?.value || false;
  }

  shouldShowStoreFilters(): boolean {
    return this.isPlanTypeCredit(); // Solo mostrar filtros de tienda para planes de crédito
  }

  getFiltersConfigSummary(): string {
    const personEnabled = this.arePersonFiltersEnabled();
    const storeEnabled = this.areStoreFiltersEnabled();

    if (personEnabled && storeEnabled) {
      return 'Filtros de persona y tienda habilitados';
    } else if (personEnabled) {
      return 'Solo filtros de persona habilitados';
    } else if (storeEnabled) {
      return 'Solo filtros de tienda habilitados';
    } else {
      return 'Sin filtros configurados';
    }
  }

  togglePersonFilters(enabled: boolean): void {
    this.step6Form.get('personConfig.enabled')?.setValue(enabled);
    console.log('🔄 Filtros de persona:', enabled ? 'Habilitados' : 'Deshabilitados');
  }

  toggleStoreFilters(enabled: boolean): void {
    this.step6Form.get('storeConfig.enabled')?.setValue(enabled);
    console.log('🔄 Filtros de tienda:', enabled ? 'Habilitados' : 'Deshabilitados');
  }

  getActiveFiltersCount(): number {
    let count = 0;
    if (this.arePersonFiltersEnabled()) count++;
    if (this.areStoreFiltersEnabled()) count++;
    return count;
  }

  // Métodos para manejo de tiendas
  filterStores(): void {
    if (!this.storeSearchTerm) {
      this.filteredStores = this.availableStores.slice(0, 10);
    } else {
      this.filteredStores = this.availableStores
        .filter(store =>
          store.name.toLowerCase().includes(this.storeSearchTerm.toLowerCase()) ||
          (store.id && store.id.toLowerCase().includes(this.storeSearchTerm.toLowerCase()))
        )
        .slice(0, 10);
    }
  }

  selectStore(store: any): void {
    const selectedStores = this.step6Form.get('storeConfig.selectedStores')?.value || [];
    if (!selectedStores.find((s: any) => s.id === store.id)) {
      selectedStores.push(store);
      this.step6Form.get('storeConfig.selectedStores')?.setValue(selectedStores);
    }
    this.storeSearchTerm = '';
    this.showStoreDropdown = false;

    // Limpiar el input
    const searchInput = document.querySelector('input[placeholder*="tienda"]') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }
  }

  removeStore(storeToRemove: any): void {
    const selectedStores = this.step6Form.get('storeConfig.selectedStores')?.value || [];
    const index = selectedStores.findIndex((s: any) => s.id === storeToRemove.id);
    if (index > -1) {
      selectedStores.splice(index, 1);
      this.step6Form.get('storeConfig.selectedStores')?.setValue(selectedStores);
    }
  }

  hideStoreDropdown(): void {
    setTimeout(() => {
      this.showStoreDropdown = false;
    }, 150);
  }

  // Métodos para filtros de productos del catálogo
  async searchCatalogProducts(): Promise<void> {
    if (!this.catalogProductSearchTerm || this.catalogProductSearchTerm.trim().length < 2) {
      this.filteredCatalogProducts = [];
      return;
    }
    this.catalogProductsLoading = true;
    try {
      const response: any = await this.userService.searchCatalogProducts(this.catalogProductSearchTerm.trim());
      this.filteredCatalogProducts = (response?.res || []).map((p: any) => ({
        code: p.code,
        name: p.name,
        brand: p.brand || '',
        category: p.category || '',
        ean_upc: p.ean_upc || '',
        total_purchases: p.stats?.total_purchases || 0
      }));
    } catch (error) {
      console.error('Error buscando productos del catálogo:', error);
      this.filteredCatalogProducts = [];
    } finally {
      this.catalogProductsLoading = false;
    }
  }

  selectCatalogProduct(product: any): void {
    const selectedProducts = this.step6Form.get('productConfig.selectedProducts')?.value || [];
    if (!selectedProducts.find((p: any) => p.code === product.code)) {
      selectedProducts.push(product);
      this.step6Form.get('productConfig.selectedProducts')?.setValue([...selectedProducts]);
    }
    this.catalogProductSearchTerm = '';
    this.showCatalogProductDropdown = false;
    this.filteredCatalogProducts = [];
  }

  addManualProductCode(): void {
    const code = (this.catalogProductSearchTerm || '').trim();
    if (!code) return;
    const selectedProducts = this.step6Form.get('productConfig.selectedProducts')?.value || [];
    if (selectedProducts.find((p: any) => p.code === code)) {
      alertifyjs.warning(`El código "${code}" ya está en la lista.`);
      this.catalogProductSearchTerm = '';
      return;
    }
    selectedProducts.push({ code, name: '', brand: '', manual: true });
    this.step6Form.get('productConfig.selectedProducts')?.setValue([...selectedProducts]);
    this.catalogProductSearchTerm = '';
    this.showCatalogProductDropdown = false;
    this.filteredCatalogProducts = [];
  }

  onProductSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.filteredCatalogProducts.length > 0) {
        this.selectCatalogProduct(this.filteredCatalogProducts[0]);
      } else {
        this.addManualProductCode();
      }
    }
  }

  removeCatalogProduct(productToRemove: any): void {
    const selectedProducts = this.step6Form.get('productConfig.selectedProducts')?.value || [];
    const index = selectedProducts.findIndex((p: any) => p.code === productToRemove.code);
    if (index > -1) {
      selectedProducts.splice(index, 1);
      this.step6Form.get('productConfig.selectedProducts')?.setValue([...selectedProducts]);
    }
  }

  removeAllCatalogProducts(): void {
    this.step6Form.get('productConfig.selectedProducts')?.setValue([]);
  }

  onExcelFileSelected(event: any): void {
    const file = event.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const selectedProducts = this.step6Form.get('productConfig.selectedProducts')?.value || [];
        let added = 0;
        let duplicates = 0;
        for (const row of rows) {
          const code = String(row[0] || '').trim();
          if (!code || code.toLowerCase() === 'codigo' || code.toLowerCase() === 'código' || code.toLowerCase() === 'code') continue;
          if (selectedProducts.find((p: any) => p.code === code)) {
            duplicates++;
            continue;
          }
          selectedProducts.push({ code, name: '', brand: '', manual: true });
          added++;
        }
        this.step6Form.get('productConfig.selectedProducts')?.setValue([...selectedProducts]);
        let msg = `Se agregaron ${added} producto(s).`;
        if (duplicates > 0) msg += ` ${duplicates} duplicado(s) omitido(s).`;
        alertifyjs.success(msg);
      } catch (err) {
        console.error('Error leyendo Excel:', err);
        alertifyjs.error('Error al leer el archivo Excel.');
      }
      event.target.value = '';
    };
    reader.readAsArrayBuffer(file);
  }

  hideCatalogProductDropdown(): void {
    setTimeout(() => {
      this.showCatalogProductDropdown = false;
    }, 150);
  }

  toggleProductFilters(enabled: boolean): void {
    this.step6Form.get('productConfig.enabled')?.setValue(enabled);
  }

  areProductFiltersEnabled(): boolean {
    return this.step6Form.get('productConfig.enabled')?.value || false;
  }

  async resolveCatalogProductNames(codes: string[]): Promise<void> {
    try {
      const resolved: any[] = [];
      for (const code of codes) {
        const response: any = await this.userService.searchCatalogProducts(code);
        const found = (response?.res || []).find((p: any) => p.code === code);
        if (found) {
          resolved.push({
            code: found.code,
            name: found.name,
            brand: found.brand || '',
            category: found.category || ''
          });
        } else {
          resolved.push({ code, name: code, brand: '', category: '' });
        }
      }
      this.step6Form.get('productConfig.selectedProducts')?.setValue(resolved);
      console.log('📦 Nombres de productos resueltos:', resolved);
    } catch (error) {
      console.error('📦 Error resolviendo nombres de productos:', error);
    }
  }

  // Métodos para el paso 6 (Otros)
  getConformityText(): string {
    const planName = this.step4Form?.get('planName')?.value || 'este plan';
    const planType = this.getSelectedPlanType() === 'credit' ? 'de crédito' : 'de contado';

    return `Al hacer clic en "Actualizar Plan", confirmo que he revisado todos los parámetros del plan "${planName}" y que la configuración es correcta. Entiendo que este plan ${planType} se actualizará en el sistema y estará sujeto a las reglas definidas en los pasos anteriores.`;
  }

  // Métodos para validación y navegación
  getMissingRequiredFields(): string[] {
    const missing: string[] = [];

    // Validar paso 1
    if (!this.validateStep1()) {
      const filterMode = this.step1Form.get('filterMode')?.value;
      const selectedTags = this.step1Form.get('selectedTags')?.value || [];
      if (filterMode === 'whitelist' && selectedTags.length === 0) {
        missing.push('Etiquetas para lista blanca');
      }
    }

    // Validar paso 2
    if (!this.validateStep2()) {
      missing.push('Productos del plan');
    }

    // Validar paso 4 (Datos Básicos)
    if (!this.validateStep4()) {
      if (!this.step4Form.get('planName')?.value) missing.push('Nombre del plan');
      if (!this.step4Form.get('planCode')?.value) missing.push('Código del plan');
      if (!this.step4Form.get('startDate')?.value) missing.push('Fecha de inicio');
    }

    // Validar paso 5 (Datos Generales - solo para crédito)
    if (this.isPlanTypeCredit() && !this.validateStep5()) {
      if (!this.step5Form.get('financing.minPurchaseAmount')?.value) {
        missing.push('Monto mínimo de compra');
      }
      if (!this.step5Form.get('financing.maxFinanceAmount')?.value) {
        missing.push('Monto máximo de financiamiento');
      }
      if (this.getInstallmentOptions().length === 0) {
        missing.push('Opciones de cuotas');
      }
    }

    return missing;
  }

  isStepCompletedSuccessfully(): boolean {
    return this.isCurrentStepValid() && this.getMissingRequiredFields().length === 0;
  }

  getNextButtonClasses(): string {
    const baseClasses = 'px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center';

    if (this.canGoToNextStep()) {
      return `${baseClasses} bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg`;
    } else {
      return `${baseClasses} bg-gray-300 text-gray-500 cursor-not-allowed`;
    }
  }

  // Métodos para el paso 7 (Resumen)
  getCurrentPlanJSONString(): string {
    return JSON.stringify(this.getDatabaseFormatJSON(), null, 2);
  }

  copyJSONToClipboard(): void {
    try {
      const jsonString = this.getCurrentPlanJSONString();
      navigator.clipboard.writeText(jsonString).then(() => {
        console.log('✅ JSON copiado al portapapeles');
      }).catch(err => {
        console.error('❌ Error al copiar JSON:', err);
      });
    } catch (error) {
      console.error('❌ Error al copiar JSON:', error);
    }
  }

  // MÉTODOS DE NAVEGACIÓN AVANZADA (copiados del wizard original)
  getNextStepNumber(currentStep: number): number {
    const visibleSteps = this.getVisibleSteps();
    const currentIndex = visibleSteps.findIndex(step => step.number === currentStep);

    if (currentIndex >= 0 && currentIndex < visibleSteps.length - 1) {
      return visibleSteps[currentIndex + 1].number;
    }

    return currentStep; // Si no hay siguiente paso, mantener el actual
  }

  getPreviousStepNumber(currentStep: number): number {
    const visibleSteps = this.getVisibleSteps();
    const currentIndex = visibleSteps.findIndex(step => step.number === currentStep);

    if (currentIndex > 0) {
      return visibleSteps[currentIndex - 1].number;
    }

    return currentStep; // Si no hay paso anterior, mantener el actual
  }

  isStepHidden(stepNumber: number): boolean {
    // El paso 5 (Datos Generales) se oculta para planes de contado
    return stepNumber === 5 && this.getSelectedPlanType() === 'cash';
  }

  canNavigateToStep(stepNumber: number): boolean {
    // En modo libre, permitir navegación a cualquier paso
    if (!this.enforceSequentialNavigation) {
      return true;
    }

    // En modo secuencial, solo permitir navegación a pasos completados o el siguiente disponible
    if (this.isStepCompleted(stepNumber)) {
      return true;
    }

    // Permitir navegación al siguiente paso disponible
    const maxAvailableStep = this.getMaxAvailableStep();
    return stepNumber <= maxAvailableStep;
  }

  private getMaxAvailableStep(): number {
    let maxStep = 1;

    // Evaluar cada paso secuencialmente
    for (let step = 1; step <= this.totalSteps; step++) {
      if (this.isStepValidForCompletion(step)) {
        maxStep = step + 1; // Permitir acceso al siguiente paso
      } else {
        break; // Detener en el primer paso inválido
      }
    }

    // No exceder el total de pasos
    return Math.min(maxStep, this.totalSteps);
  }

  private isStepValidForCompletion(stepNumber: number): boolean {
    switch (stepNumber) {
      case 1:
        return this.validateStep1();
      case 2:
        return this.validateStep2();
      case 3:
        return this.validateStep3();
      case 4:
        return this.validateStep4();
      case 5:
        // Si es plan de contado, el paso 5 (Datos Generales) se considera completado automáticamente
        if (this.getSelectedPlanType() === 'cash') {
          return true;
        }
        return this.validateStep5();
      case 6:
        return this.validateStep6();
      case 7:
        return this.validateStep7();
      case 8:
        return this.validateStep8();
      default:
        return false;
    }
  }

  // MÉTODOS DE VALIDACIÓN COMPLETOS (copiados del wizard original)
  private validateStep5(): boolean {
    console.log('🔍 Validating Step 5 (Datos Generales/Crédito)...');

    if (this.getSelectedPlanType() === 'cash') {
      console.log('✅ Cash plan - Step 5 automatically valid');
      return true; // Los planes de contado no requieren configuración del paso 5
    }

    // Validar formulario básico
    if (this.step5Form.invalid) {
      console.log('❌ Step 5 form is invalid');
      return false;
    }

    // Validaciones específicas de negocio
    const initialRequired = this.step5Form.get('initialPayment.required')?.value;
    const calculationType = this.step5Form.get('initialPayment.calculationType')?.value;
    const initialValue = this.step5Form.get('initialPayment.value')?.value;
    const minPurchaseAmount = this.step5Form.get('financing.minPurchaseAmount')?.value;
    const maxFinanceAmount = this.step5Form.get('financing.maxFinanceAmount')?.value;

    // Validar que los valores básicos estén presentes
    if (!minPurchaseAmount || minPurchaseAmount <= 0) {
      console.log('❌ Invalid minPurchaseAmount:', minPurchaseAmount);
      return false;
    }

    if (!maxFinanceAmount || maxFinanceAmount <= 0) {
      console.log('❌ Invalid maxFinanceAmount:', maxFinanceAmount);
      return false;
    }

    // Calcular el monto mínimo a financiar
    const minFinanceAmount = this.calculateMinFinanceAmount();

    // Validar que el monto máximo sea mayor al mínimo calculado
    if (maxFinanceAmount <= minFinanceAmount + 0.01) {
      console.log('❌ maxFinanceAmount <= minFinanceAmount:', maxFinanceAmount, '<=', minFinanceAmount);
      return false;
    }

    // Validar regla de oro: Si inicial es monto absoluto, debe ser menor al monto mínimo de compra
    if (initialRequired && calculationType === 'absolute' &&
        initialValue > 0 && initialValue >= minPurchaseAmount) {
      console.log('❌ Golden rule violated - absolute initial >= minPurchaseAmount:', initialValue, '>=', minPurchaseAmount);
      return false;
    }

    // Validar que las opciones de cuotas sean válidas
    const installmentOptionsString = this.step5Form.get('installments.options')?.value;
    if (!installmentOptionsString || installmentOptionsString.trim() === '') {
      console.log('❌ No installment options provided');
      return false;
    }

    const installmentOptions = this.getInstallmentOptions();
    if (installmentOptions.length === 0) {
      console.log('❌ No valid installment options after parsing');
      return false;
    }

    // Validar frecuencia de pago
    const frequency = this.step5Form.get('installments.frequency')?.value;
    if (!frequency) {
      console.log('❌ No payment frequency selected');
      return false;
    }

    console.log('✅ Step 5 validation passed');
    return true;
  }

  private validateStep6(): boolean {
    const form = this.step6Form;
    if (!form) return false;

    const filterType = form.get('filterType')?.value;

    // Si no se selecciona ningún tipo de filtro, es válido (filtros opcionales)
    if (!filterType) {
      return true;
    }

    // Validar configuración por persona
    if (filterType === 'person') {
      const personConfigEnabled = form.get('personConfig.enabled')?.value;
      if (personConfigEnabled) {
        const personType = form.get('personConfig.personType')?.value;
        if (!personType) {
          return false;
        }
      }
    }

    // Validar configuración por tienda (solo para planes de crédito)
    if (filterType === 'store' && this.isPlanTypeCredit()) {
      const storeConfigEnabled = form.get('storeConfig.enabled')?.value;
      if (storeConfigEnabled) {
        const selectedStores = form.get('storeConfig.selectedStores')?.value || [];
        const filterType = form.get('storeConfig.filterType')?.value;

        if (!filterType || selectedStores.length === 0) {
          return false;
        }
      }
    }

    return true;
  }

  private validateStep7(): boolean {
    // El paso 7 (Otros) es completamente opcional
    return true;
  }

  private validateStep8(): boolean {
    const form = this.step8Form;
    if (!form) return false;
    return form.valid;
  }

  // MÉTODOS DE CÁLCULO FINANCIERO (copiados del wizard original)
  calculateMinPurchaseAmount(): number {
    const minAmount = this.step5Form?.get('financing.minPurchaseAmount')?.value || 0;
    return Math.max(0, minAmount);
  }

  getPaymentFrequencyInDays(): number {
    const frequency = this.step5Form?.get('installments.frequency')?.value || 'mensual';
    switch (frequency) {
      case 'weekly': return 7;
      case 'quincenal': return 15;
      case 'mensual': return 30;
      default: return 30;
    }
  }

  // MÉTODOS DE NAVEGACIÓN Y ESTADO
  enableSequentialNavigation(): void {
    this.enforceSequentialNavigation = true;
    console.log('🔒 Navegación secuencial habilitada');
  }

  enableFreeNavigation(): void {
    this.enforceSequentialNavigation = false;
    console.log('🔓 Navegación libre habilitada');
  }

  getNavigationMode(): string {
    return this.enforceSequentialNavigation ? 'sequential' : 'free';
  }

  getNavigationModeDescription(): string {
    return this.enforceSequentialNavigation
      ? 'Navegación paso a paso (recomendado)'
      : 'Navegación libre (avanzado)';
  }

  getStepStatus(stepNumber: number): 'completed' | 'current' | 'pending' {
    if (this.currentStep === stepNumber) {
      return 'current';
    } else if (this.isStepCompleted(stepNumber)) {
      return 'completed';
    } else {
      return 'pending';
    }
  }

  private markStepAsCompleted(stepNumber: number): void {
    if (!this.completedSteps.includes(stepNumber)) {
      this.completedSteps.push(stepNumber);
    }
  }

  private updateStepCompletion(): void {
    // Recalcular qué pasos están completados
    for (let i = 1; i <= this.totalSteps; i++) {
      if (this.isStepValidForCompletion(i) && !this.completedSteps.includes(i)) {
        this.completedSteps.push(i);
      }
    }
  }

  // Métodos helper para campos deshabilitados
  isFieldDisabled(formGroup: FormGroup, fieldName: string): boolean {
    return formGroup.get(fieldName)?.disabled || false;
  }

  getDisabledFieldClasses(formGroup: FormGroup, fieldName: string): string {
    const baseClasses = 'w-full px-4 py-3 border rounded-lg transition-colors';
    if (this.isFieldDisabled(formGroup, fieldName)) {
      return baseClasses + ' bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed';
    }
    return baseClasses + ' border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
  }

  getDisabledSelectClasses(formGroup: FormGroup, fieldName: string): string {
    const baseClasses = 'w-full px-4 py-3 border rounded-lg transition-colors';
    if (this.isFieldDisabled(formGroup, fieldName)) {
      return baseClasses + ' bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed';
    }
    return baseClasses + ' border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
  }

  // Métodos para propuestas de seguros
  private async checkAndLoadInsuranceProposalsAfterIdentifierSelection() {
    try {
      this.addDebugLog(`🔍 Verificando configuración de seguros después de seleccionar identificador`);
      this.addDebugLog(`📋 Producto seleccionado: ${this.selectedProduct?.name} (${this.selectedProduct?.code})`);
      this.addDebugLog(`📋 Identificador seleccionado: ${this.selectedIdentifier?.name} (${this.selectedIdentifier?.code})`);

      // Obtener la configuración completa del producto para verificar si es de seguros
      const productConfig = await this.userService.getRemanenteNeteo(this.selectedProduct.code);
      this.addDebugLog(`📋 Configuración del producto recibida: ${JSON.stringify(productConfig)}`);

      if (productConfig && productConfig.insurance && productConfig.insurance.is_insurance_product) {
        this.addDebugLog(`✅ Producto ES de seguros: ${JSON.stringify(productConfig.insurance)}`);
        this.selectedProductInsuranceConfig = productConfig.insurance;
        
        // Si ya tenemos propuestas cargadas y no están vacías, no volver a cargarlas
        if (this.insuranceProposals.length === 0) {
          await this.loadInsuranceProposals(this.selectedProduct.code);
        } else {
          this.addDebugLog(`📋 Usando propuestas ya cargadas (${this.insuranceProposals.length} items)`);
        }
        
        this.showInsuranceProposals = true;
        this.addDebugLog('✅ Select de propuestas mostrado DESPUÉS de seleccionar identificador');

        // Restaurar propuesta seleccionada previamente si existe
        const productKey = `${this.selectedProduct.code}_${this.selectedIdentifier?.code || 'no_identifier'}`;
        const savedProposal = this.savedProposalState[productKey];
        if (savedProposal && savedProposal !== this.selectedProposal) {
          this.selectedProposal = savedProposal;
          this.addDebugLog(`🔄 Propuesta restaurada desde estado guardado: ${savedProposal}`);
        }
      } else {
        this.addDebugLog(`❌ Producto NO es de seguros o no tiene configuración. Insurance: ${productConfig?.insurance}`);
        this.resetInsuranceProposals();
      }
    } catch (error) {
      this.addDebugLog(`❌ Error verificando configuración de seguros después de identificador: ${error}`);
      this.resetInsuranceProposals();
    }
  }

  private async loadInsuranceProposals(productCode: string) {
    // Activar estado de carga
    this.loadingInsuranceProposals = true;
    
    try {
      // Obtener el insurance_product del identificador seleccionado
      let insuranceProductCode = 'AP'; // Valor por defecto
      
      if (this.selectedIdentifier && this.selectedIdentifier.insurance_product) {
        insuranceProductCode = this.selectedIdentifier.insurance_product;
        this.addDebugLog(`📋 Usando insurance_product del identificador: ${insuranceProductCode}`);
      } else {
        this.addDebugLog(`⚠️ Identificador no tiene insurance_product, usando valor por defecto: ${insuranceProductCode}`);
      }
      
      // Obtener el insurer_code del producto seleccionado para pasarlo como insurance_code
      let insuranceCode = null;
      if (this.selectedProduct && this.selectedProduct.insurance && this.selectedProduct.insurance.insurer_code) {
        insuranceCode = this.selectedProduct.insurance.insurer_code;
        this.addDebugLog(`📋 Usando insurance_code del producto (insurer_code): ${insuranceCode}`);
      } else {
        this.addDebugLog(`⚠️ Producto no tiene insurer_code`);
      }
      
      console.log('🔍 DEBUG - selectedIdentifier completo:', this.selectedIdentifier);
      console.log('🔍 DEBUG - insurance_product:', insuranceProductCode);
      console.log('🔍 DEBUG - selectedProduct.insurance:', this.selectedProduct?.insurance);
      console.log('🔍 DEBUG - insurance_code:', insuranceCode);
      
      this.addDebugLog(`🚀 Cargando propuestas para insurance_product: ${insuranceProductCode}, insurance_code: ${insuranceCode}`);
      const response = await this.userService.getInsuranceProposals(insuranceProductCode, insuranceCode);
      this.addDebugLog(`📦 Propuestas recibidas RAW: ${JSON.stringify(response)}`);

      let proposalsArray: any[] = [];

      if (response && response.resp) {
        proposalsArray = response.resp;
        this.addDebugLog(`✅ Propuestas desde response.resp: ${proposalsArray.length} items`);
      } else if (response && response.data) {
        proposalsArray = response.data;
        this.addDebugLog(`✅ Propuestas desde response.data: ${proposalsArray.length} items`);
      } else if (Array.isArray(response)) {
        proposalsArray = response;
        this.addDebugLog(`✅ Propuestas como array: ${proposalsArray.length} items`);
      } else if (response && typeof response === 'object') {
        // Intentar extraer de diferentes estructuras posibles
        proposalsArray = response.proposals || response.items || response.result || [];
        this.addDebugLog(`✅ Propuestas desde object property: ${proposalsArray.length} items`);
      } else {
        proposalsArray = [];
        this.addDebugLog('⚠️ No se encontraron propuestas en ninguna estructura conocida');
      }

      // Normalizar el formato de las propuestas
      this.insuranceProposals = proposalsArray.map((proposal: any) => ({
        code: proposal.id || proposal.code || proposal.cd_option || proposal._id || '',
        name: proposal.name || proposal.nombre || proposal.title || 'Sin nombre',
        description: proposal.description || proposal.descripcion || proposal.detail || '',
        mtPrima: proposal.mtPrima || proposal.mt_prima || proposal.prima || proposal.amount || '',
        deUso: proposal.deUso || proposal.de_uso || proposal.uso || ''
      })).filter((proposal: any) => proposal.code);

      this.addDebugLog(`📋 Propuestas normalizadas: ${this.insuranceProposals.length} items`);
      
      if (this.insuranceProposals.length > 0) {
        this.addDebugLog(`📋 Primera propuesta: ${JSON.stringify(this.insuranceProposals[0])}`);
      } else {
        this.addDebugLog('⚠️ No se encontraron propuestas para este producto de seguros');
        console.warn('No hay propuestas disponibles para el producto de seguros seleccionado');
      }

    } catch (error) {
      this.addDebugLog(`❌ Error cargando propuestas: ${error}`);
      console.error('Error al cargar propuestas de seguros:', error);
      this.insuranceProposals = [];
    } finally {
      // Desactivar estado de carga siempre (éxito o error)
      this.loadingInsuranceProposals = false;
    }
  }

  private resetInsuranceProposals() {
    this.insuranceProposals = [];
    this.selectedProposal = '';
    this.showInsuranceProposals = false;
    this.selectedProductInsuranceConfig = null;
  }

  onProposalChange(event: any) {
    const selectedValue = event.target.value;
    console.log('🔄 Propuesta cambiada:', {
      eventValue: selectedValue,
      selectedProposal: this.selectedProposal,
      _selectedProposal: this._selectedProposal
    });
    
    // Forzar actualización del valor
    this.selectedProposal = selectedValue;
    
    console.log('✅ Propuesta actualizada:', {
      selectedProposal: this.selectedProposal,
      _selectedProposal: this._selectedProposal
    });
  }

  private addDebugLog(message: string) {
    const timestamp = new Date().toLocaleTimeString();
    this.debugLogs.unshift(`[${timestamp}] ${message}`);
    console.log(`[DEBUG EDIT] ${message}`);

    // Mantener solo los últimos 20 logs
    if (this.debugLogs.length > 20) {
      this.debugLogs = this.debugLogs.slice(0, 20);
    }
  }

  private async checkAndRestoreInsuranceProposals(product: any) {
    try {
      this.addDebugLog(`🔄 Restaurando configuración de seguros para producto: ${product.code}`);

      // Obtener la configuración completa del producto para verificar si es de seguros
      const productConfig = await this.userService.getRemanenteNeteo(product.code);
      this.addDebugLog(`📋 Configuración del producto recibida: ${JSON.stringify(productConfig)}`);

      if (productConfig && productConfig.insurance && productConfig.insurance.is_insurance_product) {
        this.addDebugLog(`✅ Producto ES de seguros: ${JSON.stringify(productConfig.insurance)}`);
        this.selectedProductInsuranceConfig = productConfig.insurance;
        await this.loadInsuranceProposals(product.code);
        this.showInsuranceProposals = true;
        this.addDebugLog('✅ Select de propuestas mostrado para edición');
      } else {
        this.addDebugLog(`❌ Producto NO es de seguros o no tiene configuración`);
        this.resetInsuranceProposals();
      }
    } catch (error) {
      this.addDebugLog(`❌ Error verificando configuración de seguros en edición: ${error}`);
      this.resetInsuranceProposals();
    }
  }

  /**
   * Verifica si el producto seleccionado es de seguros
   */
  isInsuranceProduct(): boolean {
    // Verificar si hay producto seleccionado con is_insurance_product
    if (this.selectedProduct && this.selectedProduct.insurance && this.selectedProduct.insurance.is_insurance_product) {
      return true;
    }
    
    // Verificar en addedProducts si existe al menos un producto de seguros
    if (this.addedProducts && this.addedProducts.length > 0) {
      return this.addedProducts.some((item: any) => 
        item.product && item.product.insurance && item.product.insurance.is_insurance_product
      );
    }
    
    return false;
  }

  /**
   * Actualiza las validaciones del campo prioridad según si es producto de seguros o no
   */
  private updatePriorityValidation(): void {
    const priorityControl = this.step4Form?.get('priority');
    if (!priorityControl) return;

    if (this.isInsuranceProduct()) {
      // Para productos de seguros: solo validar rango
      priorityControl.setValidators([Validators.min(0), Validators.max(999)]);
      console.log('✅ Prioridad configurada como OPCIONAL (producto de seguros)');
    } else {
      // Para otros productos: requerido + rango
      priorityControl.setValidators([Validators.required, Validators.min(0), Validators.max(999)]);
      console.log('✅ Prioridad configurada como REQUERIDA (producto normal)');
    }
    
    priorityControl.updateValueAndValidity();
  }

  /**
   * Callback cuando cambian los payment terms del builder
   */
  onPaymentTermsChanged(terms: any[]): void {
    this.paymentTerms = terms;
    console.log('📋 Payment terms actualizados:', this.paymentTerms);
  }

  /**
   * Callback cuando cambia la validez de los payment terms
   */
  onPaymentTermsValidityChanged(isValid: boolean): void {
    this.paymentTermsValid = isValid;
    console.log('✅ Payment terms válidos:', this.paymentTermsValid);
  }

  /**
   * Generar payment_terms desde el PaymentTermsBuilder
   */
  private generatePaymentTermsFromBuilder(): any[] {
    console.log('🔄 Generando payment_terms desde builder...');

    if (!this.paymentTerms || this.paymentTerms.length === 0) {
      console.log('⚠️ No hay payment terms configurados');
      return [];
    }

    // Convertir formato del builder al formato de base de datos
    const paymentTerms = this.paymentTerms.map((term: any) => {
      const code = `${this.planCode}_${term.installments}`;
      const name = term.frequency === 'dias_continuos'
        ? `${term.installments} cuotas pagadas cada ${term.frequencyDays} días`
        : `${term.installments} cuotas pagadas ${this.mapFrequencyToSpanish(term.frequency)}`;

      return {
        code: code,
        frequency: {
          type: term.frequency,
          ...(term.frequency === 'dias_continuos' && { number: term.frequencyDays })
        },
        name: name,
        order: term.order,
        installments: term.installments
      };
    });

    console.log('✅ Payment terms generados desde builder:', paymentTerms);
    return paymentTerms;
  }

}
